import type { Locale } from "./i18n"
import { isValidLocale } from "./i18n"

type Translations = {
  home: {
    title: string
    description: string
    keywords: string
  }
  nav: {
    services: string
    corporate: string
    references: string
    blog: string
    contact: string
    getQuote: string
  }
  hero: {
    slides: Array<{
      badge: string
      highlight: string
      subtitle: string
      description: string
      ctaLink: string
    }>
    freeQuote: string
    viewService: string
    prevSlide: string
    nextSlide: string
  }
  services: {
    badge: string
    title: string
    description: string
    items: Array<{
      title: string
      description: string
      href: string
      code: string
    }>
    moreInfo: string
    viewAll: string
  }
  cta: {
    title: string
    titleHighlight: string
    description: string
    button: string
    stats: {
      projects: string
      satisfaction: string
      experience: string
    }
  }
  references: {
    badge: string
    title: string
    description: string
    loading: string
    empty: string
    viewCaseStudy: string
    viewAll: string
    previous: string
    next: string
    imagePlaceholder: string
  }
  blog: {
    title: string
    description: string
    loading: string
    empty: string
    readMore: string
    viewAll: string
    noImage: string
  }
  header: {
    services: string
    corporate: string
    references: string
    products: string
    blog: string
    contact: string
    getQuote: string
    menuOpen: string
    menuClose: string
    serviceCategories: {
      webDevelopment: string
      mobileDesktop: string
      eCommerce: string
      automationAI: string
    }
    serviceItems: {
      webDesign: { label: string; description: string }
      webDevelopment: { label: string; description: string }
      mobileApp: { label: string; description: string }
      desktopApp: { label: string; description: string }
      eCommerce: { label: string; description: string }
      shopify: { label: string; description: string }
      woocommerce: { label: string; description: string }
      automation: { label: string; description: string }
      aiServices: { label: string; description: string }
      uiUx: { label: string; description: string }
    }
    corporateItems: {
      about: string
      corporateIdentity: string
      laki: string
      career: string
    }
  }
  footer: {
    sections: {
      webDevelopment: string
      mobileDesktop: string
      eCommerce: string
      automationAI: string
      corporate: string
      resources: string
    }
    newsletter: {
      title: string
      description: string
    }
    copyright: string
    allRightsReserved: string
    privacyPolicy: string
    kvkk: string
    termsOfUse: string
    cookiePolicy: string
  }
  quoteForm: {
    title: string
    description: string
    personalInfo: string
    projectDetails: string
    name: string
    company: string
    email: string
    phone: string
    service: string
    selectService: string
    deadline: string
    selectDeadline: string
    message: string
    send: string
    sending: string
    success: string
    error: string
    deadlineOptions: {
      "0-2": string
      "2-4": string
      "4-8": string
      "8-10": string
      "10+": string
    }
    services: {
      webDesign: string
      webDevelopment: string
      mobileApp: string
      desktopApp: string
      eCommerce: string
      shopify: string
      woocommerce: string
      automation: string
      aiServices: string
      uiUx: string
    }
  }
  newsletter: {
    placeholder: string
    button: string
    sending: string
    sent: string
    success: string
    error: string
    invalidEmail: string
  }
  cookieConsent: {
    title: string
    description: string
    cookiePolicyLink: string
    reject: string
    accept: string
  }
  scrollToTop: {
    label: string
    ariaLabel: string
  }
  dreamAssistant: {
    title: string
    description: string
    placeholder: string
    submit: string
    analyzing: string
    analyzingSubtext: string
    storyTitle: string
    servicesTitle: string
    featuresTitle: string
    complexityTitle: string
    nextStepsTitle: string
    share: string
    shareSuccess: string
    getQuote: string
    tryAgain: string
    error: string
    rateLimitError: string
    complexity: {
      low: string
      medium: string
      high: string
      "very-high": string
    }
  }
  admin: {
    dashboard: {
      title: string
      description: string
      stats: {
        totalProjects: string
        activeProjects: string
        completedProjects: string
        totalCustomers: string
      }
    }
    projects: {
      title: string
      newProject: string
      editProject: string
      noProjects: string
      loading: string
      fields: {
        title: string
        client: string
        description: string
        deadline: string
        status: string
        progress: string
        locale: string
        customer: string
      }
      status: {
        inProgress: string
        review: string
        completed: string
        onHold: string
      }
      actions: {
        save: string
        cancel: string
        delete: string
        edit: string
        view: string
      }
      messages: {
        createSuccess: string
        updateSuccess: string
        deleteSuccess: string
        createError: string
        updateError: string
        deleteError: string
      }
    }
    references: {
      title: string
      newReference: string
      editReference: string
    }
    blog: {
      title: string
      newPost: string
      editPost: string
    }
  }
  pages: {
    about: {
      title: string
      description: string
      hero: {
        title: string
        subtitle: string
      }
      sections: {
        intro: {
          title: string
          paragraphs: string[]
        }
        mission: {
          title: string
          badge: string
          description: string
          principles: string[]
        }
        vision: {
          title: string
          badge: string
          description: string
          principles: string[]
        }
        values: {
          title: string
          badge: string
          description: string
          principles: string[]
        }
        experience: {
          title: string
          badge: string
          description: string
          principles: string[]
        }
        principlesTitle: string
        importance: {
          title: string
          description: string
        }
      }
    }
    corporateIdentity: {
      title: string
      description: string
      hero: {
        title: string
        subtitle: string
      }
      logo: {
        title: string
        subtitle: string
        dark: {
          title: string
          description: string
        }
        light: {
          title: string
          description: string
        }
      }
      colors: {
        title: string
        subtitle: string
        primary: {
          title: string
          description: string
        }
        accent: {
          title: string
          description: string
        }
      }
      typography: {
        title: string
        subtitle: string
        mainFont: string
        examples: {
          light: string
          regular: string
          bold: string
        }
      }
      brandValues: {
        title: string
        subtitle: string
        items: Array<{
          title: string
          description: string
        }>
      }
    }
    career: {
      hero: {
        title: string
        subtitle: string
      }
      whyJoin: {
        title: string
        subtitle: string
        items: Array<{
          title: string
          description: string
        }>
      }
      openings: {
        title: string
        noOpenings: string
        generalApplication: {
          title: string
          description: string
          button: string
        }
      }
    }
    careerApplication: {
      hero: {
        title: string
        subtitle: string
      }
      sections: {
        personalInfo: string
        professionalInfo: string
        resume: string
        coverLetter: string
      }
      fields: {
        name: string
        email: string
        phone: string
        position: string
        selectPosition: string
        experience: string
        selectExperience: string
        linkedin: string
        portfolio: string
        resumeUpload: string
        resumePlaceholder: string
        resumeMaxSize: string
        coverLetterLabel: string
        coverLetterPlaceholder: string
      }
      experienceOptions: {
        "0-1": string
        "1-3": string
        "3-5": string
        "5-7": string
        "7+": string
      }
      positionOptions: {
        seniorFrontend: string
        uiUx: string
        mobileApp: string
        aiMl: string
        backend: string
        fullstack: string
        general: string
      }
      buttons: {
        back: string
        submit: string
        submitting: string
      }
      messages: {
        resumeRequired: string
        submitError: string
        submitSuccess: string
      }
    }
    products: {
      title: string
      description: string
      hero: {
        title: string
        subtitle: string
      }
      intro: {
        title: string
        description: string
      }
      comingSoon: string
      subscriptionBased: string
      items: Array<{
        title: string
        description: string
        features: string[]
      }>
    }
    quoteRequest: {
      hero: {
        title: string
        subtitle: string
        benefits: Array<{
          text: string
        }>
      }
      sections: {
        personalInfo: string
        projectDetails: string
        projectAbout: string
      }
      fields: {
        name: string
        company: string
        email: string
        phone: string
        service: string
        selectService: string
        deadline: string
        selectDeadline: string
        message: string
        messagePlaceholder: string
      }
      buttons: {
        submit: string
        submitting: string
      }
      messages: {
        error: string
        success: string
      }
    }
    contact: {
      hero: {
        title: string
        subtitle: string
      }
      form: {
        title: string
        subtitle: string
        fields: {
          name: string
          email: string
          phone: string
          subject: string
          message: string
        }
        button: string
        submitting: string
      }
      info: {
        contactInfo: string
        workingHours: string
        followUs: string
        hours: {
          weekdays: string
          saturday: string
          sunday: string
        }
      }
      messages: {
        error: string
        success: string
      }
    }
    privacyPolicy: {
      title: string
      description: string
      lastUpdated: string
      sections: {
        general: {
          title: string
          paragraphs: string[]
        }
        dataController: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          web: string
        }
        collectedData: {
          title: string
          intro: string
          identity: {
            title: string
            items: string[]
          }
          contact: {
            title: string
            items: string[]
          }
          transaction: {
            title: string
            items: string[]
          }
          technical: {
            title: string
            items: string[]
          }
        }
        processingPurposes: {
          title: string
          intro: string
          items: string[]
        }
        legalBasis: {
          title: string
          intro: string
          items: string[]
        }
        dataSharing: {
          title: string
          paragraphs: string[]
          items: string[]
        }
        cookies: {
          title: string
          paragraphs: string[]
        }
        retention: {
          title: string
          paragraphs: string[]
        }
        security: {
          title: string
          intro: string
          items: string[]
        }
        rights: {
          title: string
          intro: string
          items: string[]
        }
        application: {
          title: string
          paragraphs: string[]
        }
        changes: {
          title: string
          paragraphs: string[]
        }
        contact: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          web: string
        }
      }
    }
    cookiePolicy: {
      title: string
      description: string
      lastUpdated: string
      sections: {
        whatIs: {
          title: string
          paragraphs: string[]
        }
        purpose: {
          title: string
          intro: string
          items: string[]
        }
        types: {
          title: string
          required: {
            title: string
            paragraphs: string[]
            examples: {
              title: string
              items: string[]
            }
          }
          analytical: {
            title: string
            paragraphs: string[]
            tools: {
              title: string
              items: string[]
            }
          }
          functional: {
            title: string
            paragraphs: string[]
            examples: {
              title: string
              items: string[]
            }
          }
          marketing: {
            title: string
            paragraphs: string[]
            examples: {
              title: string
              items: string[]
            }
          }
        }
        thirdParty: {
          title: string
          paragraphs: string[]
          services: {
            title: string
            items: string[]
          }
        }
        retention: {
          title: string
          intro: string
          items: Array<{
            type: string
            duration: string
          }>
        }
        management: {
          title: string
          paragraphs: string[]
          browserSettings: {
            title: string
            intro: string
            browsers: Array<{
              name: string
              instructions: string
            }>
          }
          tools: {
            title: string
            paragraphs: string[]
          }
        }
        disabling: {
          title: string
          intro: string
          items: string[]
        }
        mobile: {
          title: string
          intro: string
          items: Array<{
            platform: string
            instructions: string
          }>
        }
        changes: {
          title: string
          paragraphs: string[]
        }
        contact: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          web: string
        }
      }
    }
    termsOfUse: {
      title: string
      description: string
      lastUpdated: string
      sections: {
        general: {
          title: string
          paragraphs: string[]
        }
        services: {
          title: string
          intro: string
          items: string[]
        }
        obligations: {
          title: string
          intro: string
          items: string[]
        }
        intellectualProperty: {
          title: string
          paragraphs: string[]
        }
        contracts: {
          title: string
          paragraphs: string[]
        }
        warranty: {
          title: string
          paragraphs: string[]
        }
        thirdParty: {
          title: string
          paragraphs: string[]
        }
        dataSecurity: {
          title: string
          paragraphs: string[]
        }
        cancellation: {
          title: string
          paragraphs: string[]
        }
        changes: {
          title: string
          paragraphs: string[]
        }
        law: {
          title: string
          paragraphs: string[]
        }
        contact: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          phone: string
          web: string
        }
      }
    }
    kvkk: {
      title: string
      description: string
      subtitle: string
      lastUpdated: string
      sections: {
        dataController: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          web: string
        }
        processedData: {
          title: string
          intro: string
          identity: {
            title: string
            items: string[]
          }
          contact: {
            title: string
            items: string[]
          }
          customer: {
            title: string
            items: string[]
          }
          marketing: {
            title: string
            items: string[]
          }
          security: {
            title: string
            items: string[]
          }
          media: {
            title: string
            items: string[]
          }
        }
        processingPurposes: {
          title: string
          intro: string
          items: string[]
        }
        legalBasis: {
          title: string
          intro: string
          items: Array<{
            law: string
            description: string
          }>
        }
        dataTransfer: {
          title: string
          paragraphs: string[]
          domestic: {
            title: string
            items: string[]
          }
          international: {
            title: string
            paragraphs: string[]
          }
        }
        collection: {
          title: string
          intro: string
          items: string[]
        }
        cookies: {
          title: string
          paragraphs: string[]
          types: {
            title: string
            items: Array<{
              type: string
              description: string
            }>
          }
          management: {
            title: string
            paragraphs: string[]
          }
        }
        retention: {
          title: string
          intro: string
          items: Array<{
            type: string
            duration: string
          }>
          note: string
        }
        security: {
          title: string
          intro: string
          items: string[]
        }
        rights: {
          title: string
          intro: string
          items: Array<{
            title: string
            description: string
          }>
        }
        application: {
          title: string
          paragraphs: string[]
          email: {
            title: string
            address: string
          }
          written: {
            title: string
            address: string
          }
          note: string
          complaint: string
        }
        changes: {
          title: string
          paragraphs: string[]
        }
        contact: {
          title: string
          paragraphs: string[]
          company: string
          email: string
          web: string
        }
      }
    }
    services: {
      common: {
        features: string
        process: {
          title: string
          subtitle: string
        }
        steps: Array<{
          title: string
          description: string
        }>
      }
      webDesign: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      webDevelopment: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      mobileApp: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      desktopApp: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      eCommerce: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      shopify: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      woocommerce: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      automation: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      aiServices: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
      uiUx: {
        title: string
        description: string
        hero: {
          title: string
          subtitle: string
        }
        features: string[]
        content: {
          title: string
          paragraphs: string[]
        }
      }
    }
  }
  // Add more translation keys as needed
}

const translations: Record<Locale, Translations> = {
  tr: {
    home: {
      title: "Ana Sayfa",
      description: "LAKVERSE - Profesyonel web tasarım, mobil uygulama, e-ticaret ve yapay zeka destekli dijital dönüşüm hizmetleri. Modern teknolojilerle markanızı dijital dünyada öne çıkarıyoruz.",
      keywords: "web tasarım, web yazılım, ui ux tasarım, mobil uygulama, e-ticaret, yapay zeka, dijital dönüşüm, yazılım ajansı, Shopify, WooCommerce, Next.js, React",
    },
    nav: {
      services: "Hizmetler",
      corporate: "Kurumsal",
      references: "Referanslar",
      blog: "Blog",
      contact: "İletişim",
      getQuote: "Teklif Al",
    },
    hero: {
      slides: [
        {
          badge: "Web Tasarım",
          highlight: "dijital dünyada",
          subtitle: "öne çıkın",
          description: "Modern ve şık web tasarımları ile markanızı dijital dünyada öne çıkarın. Kullanıcı deneyimi odaklı çözümlerle işletmenizi bir adım öne taşıyoruz.",
          ctaLink: "/tr/hizmetler/web-tasarim",
        },
        {
          badge: "Web Yazılım",
          highlight: "profesyonel web",
          subtitle: "çözümleri sunuyor",
          description: "Hızlı, güvenli ve kullanıcı dostu web uygulamaları geliştiriyoruz. İşletmenizin büyümesine ayak uydurabilecek modern çözümler.",
          ctaLink: "/tr/hizmetler/web-yazilim",
        },
        {
          badge: "Mobil Uygulama",
          highlight: "mobil uygulamalarla",
          subtitle: "müşterilerinize ulaşın",
          description: "iPhone ve Android uygulamaları ile müşterilerinize her zaman, her yerde ulaşın. Hızlı ve kullanıcı dostu çözümler.",
          ctaLink: "/tr/hizmetler/mobil-uygulama",
        },
        {
          badge: "E-Ticaret",
          highlight: "e-ticaret ile",
          subtitle: "satışlarınızı artırın",
          description: "Shopify, WooCommerce ve özel e-ticaret çözümleriyle online satış kanallarınızı güçlendirin.",
          ctaLink: "/tr/hizmetler/e-ticaret",
        },
        {
          badge: "Yapay Zeka",
          highlight: "yapay zeka ile",
          subtitle: "geleceği şekillendirin",
          description: "AI destekli otomasyon ve akıllı çözümlerle iş süreçlerinizi optimize edin, verimliliğinizi artırın.",
          ctaLink: "/tr/hizmetler/ai-servisler",
        },
      ],
      freeQuote: "Ücretsiz Teklif Al",
      viewService: "Hizmeti İncele",
      prevSlide: "Önceki slide",
      nextSlide: "Sonraki slide",
    },
    services: {
      badge: "Hizmetler",
      title: "Hizmetlerimiz",
      description: "Dijital dönüşüm yolculuğunuzda size eşlik eden kapsamlı hizmetlerimiz. Modern teknolojiler ve yapay zeka destekli çözümlerle işletmenizi bir adım öne taşıyoruz.",
      items: [
        {
          title: "Web Tasarım",
          description: "Modern, kullanıcı dostu ve Google'da üst sıralarda görünen web tasarımları. Markanızın dijital kimliğini yansıtan, satışlarınızı artıran profesyonel web siteleri tasarlıyoruz.",
          href: "/tr/hizmetler/web-tasarim",
          code: "const design = createModernUI();",
        },
        {
          title: "Web Yazılım",
          description: "İşletmenize özel, hızlı ve güvenli web yazılımları. Yönetim panellerinden müşteri portallarına, büyümenize ayak uyduran ölçeklenebilir çözümler geliştiriyoruz.",
          href: "/tr/hizmetler/web-yazilim",
          code: "function buildApp() { return <App />; }",
        },
        {
          title: "Mobil Uygulama",
          description: "iPhone ve Android telefonlar için hızlı ve kullanıcı dostu mobil uygulamalar. Müşterilerinizin kolayca kullanabileceği, güvenilir mobil çözümler sunuyoruz.",
          href: "/tr/hizmetler/mobil-uygulama",
          code: "const app = new MobileApp();",
        },
        {
          title: "E-ticaret Sistemleri",
          description: "Shopify, WooCommerce ve özel e-ticaret çözümleri. Satışlarınızı artıran, kullanıcı dostu ve güvenli online mağazalar kuruyoruz.",
          href: "/tr/hizmetler/e-ticaret",
          code: "shopify.init({ store: 'yours' });",
        },
        {
          title: "Otomasyon",
          description: "Tekrarlayan iş süreçlerinizi otomatikleştiren akıllı çözümler. Sistemlerinizi birbirine bağlayan, zaman ve maliyet tasarrufu sağlayan otomasyonlar kuruyoruz.",
          href: "/tr/hizmetler/otomasyon",
          code: "workflow.trigger('automation');",
        },
        {
          title: "AI Destekli Servisler",
          description: "Yapay zeka teknolojileri ile fotoğraf, video ve metin üretimi. İşletmenizin ihtiyaçlarına özel AI entegrasyonları ve ürünleştirilmiş servisler sunuyoruz.",
          href: "/tr/hizmetler/ai-servisler",
          code: "ai.generate({ type: 'content' });",
        },
      ],
      moreInfo: "Daha Fazla Bilgi",
      viewAll: "Tüm Hizmetleri Gör",
    },
    cta: {
      title: "Birlikte ",
      titleHighlight: "Çalışalım",
      description: "Projeniz için ücretsiz teklif alın. Dijital dönüşüm yolculuğunuzda size yardımcı olmaktan mutluluk duyarız. Modern teknolojiler ve profesyonel ekibimizle işletmenizi bir adım öne taşıyoruz.",
      button: "Ücretsiz Teklif Al",
      stats: {
        projects: "Proje",
        satisfaction: "Memnuniyet",
        experience: "Yıl Deneyim",
      },
    },
    references: {
      badge: "Portfolio",
      title: "Referanslarımız",
      description: "Başarılı projelerimizden örnekler. Müşterilerimizin dijital dönüşüm hikayelerini keşfedin ve markanız için ilham alın.",
      loading: "Yükleniyor...",
      empty: "Henüz referans bulunmuyor.",
      viewCaseStudy: "Vaka Çalışmasını İncele",
      viewAll: "Tüm Referansları Gör",
      previous: "Önceki",
      next: "Sonraki",
      imagePlaceholder: "Görsel Eklenecek",
    },
    blog: {
      title: "Blog & İçerikler",
      description: "Dijital dünyadan son haberler, trendler ve uzman görüşleri. Web tasarım, e-ticaret, yapay zeka ve dijital pazarlama konularında güncel içerikler.",
      loading: "Yükleniyor...",
      empty: "Henüz blog yazısı bulunmuyor.",
      readMore: "Devamını Oku",
      viewAll: "Tüm Yazıları Gör",
      noImage: "Görsel Yok",
    },
    newsletter: {
      placeholder: "E-posta adresiniz",
      button: "Abone Ol",
      sending: "Gönderiliyor...",
      sent: "Gönderildi!",
      success: "Başarıyla abone oldunuz! Teşekkürler.",
      error: "Bir hata oluştu. Lütfen tekrar deneyin.",
      invalidEmail: "Lütfen geçerli bir e-posta adresi girin.",
    },
    admin: {
      dashboard: {
        title: "Admin Paneli",
        description: "Projelerinizi ve müşterilerinizi yönetin",
        stats: {
          totalProjects: "Toplam Proje",
          activeProjects: "Aktif Projeler",
          completedProjects: "Tamamlanan Projeler",
          totalCustomers: "Toplam Müşteri",
        },
      },
      projects: {
        title: "Projeler",
        newProject: "Yeni Proje",
        editProject: "Projeyi Düzenle",
        noProjects: "Henüz proje bulunmuyor.",
        loading: "Yükleniyor...",
        fields: {
          title: "Proje Başlığı",
          client: "Müşteri",
          description: "Açıklama",
          deadline: "Teslim Tarihi",
          status: "Durum",
          progress: "İlerleme (%)",
          locale: "Dil",
          customer: "Müşteri Seçin",
        },
        status: {
          inProgress: "Devam Ediyor",
          review: "İncelemede",
          completed: "Tamamlandı",
          onHold: "Beklemede",
        },
        actions: {
          save: "Kaydet",
          cancel: "İptal",
          delete: "Sil",
          edit: "Düzenle",
          view: "Görüntüle",
        },
        messages: {
          createSuccess: "Proje başarıyla oluşturuldu.",
          updateSuccess: "Proje başarıyla güncellendi.",
          deleteSuccess: "Proje başarıyla silindi.",
          createError: "Proje oluşturulurken bir hata oluştu.",
          updateError: "Proje güncellenirken bir hata oluştu.",
          deleteError: "Proje silinirken bir hata oluştu.",
        },
      },
      references: {
        title: "Referanslar",
        newReference: "Yeni Referans",
        editReference: "Referansı Düzenle",
      },
      blog: {
        title: "Blog",
        newPost: "Yeni Yazı",
        editPost: "Yazıyı Düzenle",
      },
    },
    cookieConsent: {
      title: "Çerezleri Kullanıyoruz",
      description: "Web sitemizde size en iyi deneyimi sunabilmek ve site kullanımınızı analiz edebilmek için çerezler kullanıyoruz. Çerezler hakkında detaylı bilgi için",
      cookiePolicyLink: "Çerez Politikası",
      reject: "Reddet",
      accept: "Kabul Et",
    },
    scrollToTop: {
      label: "Yukarı",
      ariaLabel: "Yukarı çık",
    },
    header: {
      services: "Hizmetler",
      corporate: "Kurumsal",
      references: "Referanslar",
      products: "Ürünlerimiz",
      blog: "Blog",
      contact: "İletişim",
      getQuote: "Teklif Al",
      menuOpen: "Menüyü aç",
      menuClose: "Menüyü kapat",
      serviceCategories: {
        webDevelopment: "WEB GELİŞTİRME",
        mobileDesktop: "MOBİL & MASAÜSTÜ",
        eCommerce: "E-TİCARET",
        automationAI: "OTOMASYON & AI",
      },
      serviceItems: {
        webDesign: { label: "Web Tasarım", description: "Modern ve şık web tasarımları" },
        webDevelopment: { label: "Web Yazılım", description: "İşletmenize özel web yazılımları" },
        mobileApp: { label: "Mobil Uygulama", description: "iPhone ve Android uygulamaları" },
        desktopApp: { label: "Masaüstü Uygulama", description: "Windows, Mac ve Linux uygulamaları" },
        eCommerce: { label: "E-ticaret", description: "Özel e-ticaret çözümleri" },
        shopify: { label: "Shopify", description: "Shopify mağaza tasarımı" },
        woocommerce: { label: "WooCommerce", description: "WooCommerce entegrasyonları" },
        automation: { label: "Otomasyon", description: "İş süreçlerini otomatikleştirin" },
        aiServices: { label: "AI Servisler", description: "Yapay zeka destekli çözümler" },
        uiUx: { label: "UI & UX Tasarım", description: "Kullanıcı odaklı arayüz ve deneyim tasarımı" },
      },
      corporateItems: {
        about: "Hakkımızda",
        corporateIdentity: "Kurumsal Kimlik",
        laki: "Laki ile Tanışın",
        career: "Kariyer",
      },
    },
    footer: {
      sections: {
        webDevelopment: "Web Geliştirme",
        mobileDesktop: "Mobil & Masaüstü",
        eCommerce: "E-Ticaret",
        automationAI: "Otomasyon & AI",
        corporate: "Kurumsal",
        resources: "Kaynaklar",
      },
      newsletter: {
        title: "Haberlerden Haberdar Olun",
        description: "Yeni projelerimiz, teknoloji haberleri ve özel tekliflerden haberdar olmak için abone olun.",
      },
      copyright: "©",
      allRightsReserved: "Tüm hakları saklıdır.",
      privacyPolicy: "Gizlilik Politikası",
      kvkk: "KVKK",
      termsOfUse: "Kullanım Koşulları",
      cookiePolicy: "Çerez Politikası",
    },
    pages: {
      about: {
        title: "Hakkımızda | LAKVERSE - Modern Yazılım Çözümleri",
        description: "LAKVERSE hakkında bilgiler. Web tasarım, mobil uygulama geliştirme, e-ticaret çözümleri ve AI destekli otomasyon sistemleri konularında uzmanlaşmış yazılım ajansı.",
        hero: {
          title: "Hakkımızda",
          subtitle: "Modern yazılım çözümleri ile işinizi dijital dünyaya taşıyoruz",
        },
        sections: {
          intro: {
            title: "Dijital Dönüşümde Güvenilir Partneriniz",
            paragraphs: [
              "LAKVERSE olarak, web tasarım, mobil uygulama geliştirme, e-ticaret çözümleri ve AI destekli otomasyon sistemleri konularında uzmanlaşmış bir yazılım ajansıyız. Müşterilerimize en iyi teknolojik çözümleri sunarak, dijital dönüşüm yolculuklarında yanlarında oluyoruz.",
              "Next.js, React, Supabase gibi modern teknolojiler kullanarak, ölçeklenebilir ve performanslı uygulamalar geliştiriyoruz. Ekibimiz, yazılım geliştirme, tasarım, proje yönetimi ve müşteri ilişkileri konularında deneyimli profesyonellerden oluşmaktadır.",
              "Her projede, müşterilerimizin ihtiyaçlarını anlamak ve en uygun çözümleri sunmak için detaylı analizler yapıyoruz. Müşteri memnuniyeti ve proje kalitesi bizim için en öncelikli değerlerdir.",
              "Web tasarım sürecimiz, markanızın hedef kitlesini anlamakla başlar. Kullanıcı araştırmaları, rakip analizleri ve sektör trendlerini inceleyerek, markanıza özel bir tasarım stratejisi geliştiriyoruz.",
            ],
          },
          mission: {
            title: "Misyonumuz",
            badge: "Temel Değer",
            description: "LAKVERSE olarak, müşterilerimize en yüksek kalitede yazılım çözümleri sunmak temel hedefimizdir. Dijital dönüşüm yolculuklarında yanlarında olmak ve en iyi teknolojik çözümleri sunmak.",
            principles: [
              "Müşteri memnuniyetini ön planda tutmak",
              "Sürekli iyileştirme anlayışı ile çalışmak",
              "Modern teknolojiler ve en iyi pratikleri kullanmak",
              "Deneyimli ve eğitimli personel ile çalışmak",
              "Proje kalitesini titizlikle yürütmek",
            ],
          },
          vision: {
            title: "Vizyonumuz",
            badge: "Gelecek Hedefi",
            description: "Türkiye'nin önde gelen yazılım ajansı olmak ve AI teknolojileri ile sektörde öncü olmak. Dijital dönüşümde referans gösterilen bir marka haline gelmek.",
            principles: [
              "Sektörde lider konumda olmak",
              "Yenilikçi teknolojileri öncü olarak kullanmak",
              "Müşteri başarısına odaklanmak",
              "Sürekli gelişim ve öğrenme kültürü",
              "Dijital dönüşümde referans olmak",
            ],
          },
          values: {
            title: "Değerlerimiz",
            badge: "Kurumsal Kültür",
            description: "Müşteri odaklılık, yenilikçilik, şeffaflık ve kalite odaklı çalışma prensiplerimiz. Her projede en yüksek standartları hedefliyoruz.",
            principles: [
              "Müşteri odaklı yaklaşım",
              "Yenilikçilik ve yaratıcılık",
              "Şeffaflık ve dürüstlük",
              "Kalite ve mükemmellik",
              "Takım çalışması ve işbirliği",
            ],
          },
          experience: {
            title: "Deneyim",
            badge: "Sektör Lideri",
            description: "Yılların getirdiği deneyim ve başarılı projelerle sektörde güvenilir bir partneriz. Sürekli öğrenme ve gelişim kültürümüzle sektörde öncüyüz.",
            principles: [
              "Yılların deneyimi ve uzmanlık",
              "Başarılı proje portföyü",
              "Sürekli öğrenme ve gelişim",
              "Sektörde güvenilir partner",
              "İnovatif çözümler ve yaklaşımlar",
            ],
          },
          principlesTitle: "TEMEL PRENSİPLERİ",
          importance: {
            title: "Değerlerimizin Önemi",
            description: "LAKVERSE olarak, tüm değerlerimizi şeffaf ve sorumlu bir şekilde yürütmekteyiz. Bu değerler, iş yapış şekillerimizin temelini oluşturur ve müşterilerimiz, çalışanlarımız ve toplum için sürdürülebilir değer yaratmamızı sağlar.",
          },
        },
      },
      corporateIdentity: {
        title: "Kurumsal Kimlik | LAKVERSE - Marka Kimliği ve Tasarım",
        description: "LAKVERSE kurumsal kimlik, logo kullanımı, renk paleti, tipografi ve marka değerleri. Profesyonel yazılım ajansı olarak kurumsal kimlik standartlarımız.",
        hero: {
          title: "Kurumsal Kimlik",
          subtitle: "Markamızın görsel kimliği ve kurumsal standartlarımız. LAKVERSE olarak, tutarlı ve profesyonel bir marka deneyimi sunuyoruz.",
        },
        logo: {
          title: "Logo Kullanımı",
          subtitle: "Logo'muz, markamızın en önemli görsel öğesidir. Doğru kullanımı marka bütünlüğümüz için kritiktir.",
          dark: {
            title: "Koyu Arka Plan",
            description: "Beyaz logo, koyu arka planlar üzerinde kullanılır. Minimum boşluk alanı logo yüksekliğinin yarısı kadar olmalıdır.",
          },
          light: {
            title: "Açık Arka Plan",
            description: "Renkli logo, açık arka planlar üzerinde kullanılır. Logo'nun etrafında yeterli boşluk bırakılmalıdır.",
          },
        },
        colors: {
          title: "Renk Paleti",
          subtitle: "Markamızın renk paleti, tutarlı ve profesyonel bir görsel kimlik oluşturmak için tasarlanmıştır.",
          primary: {
            title: "Ana Renk",
            description: "Ana marka rengi, kurumsal kimliğimizin temelini oluşturur. Logo, başlıklar ve vurgu öğelerinde kullanılır.",
          },
          accent: {
            title: "Vurgu Rengi",
            description: "Vurgu rengi, ikincil öğeler, hover efektleri ve gradyan arka planlarda kullanılır.",
          },
        },
        typography: {
          title: "Tipografi",
          subtitle: "Markamızın tipografik kimliği, okunabilirlik ve modern görünümü bir araya getirir.",
          mainFont: "Ana Font: JetBrains Mono",
          examples: {
            light: "İnce vurgular",
            regular: "Varsayılan metin",
            bold: "Başlıklar",
          },
        },
        brandValues: {
          title: "Marka Değerleri",
          subtitle: "LAKVERSE olarak, bu değerler markamızın temelini oluşturur ve tüm çalışmalarımızda rehberimizdir.",
          items: [
            {
              title: "Güvenilirlik",
              description: "İş ortaklarımızla kurduğumuz güçlü ilişkiler ve sözümüzde durma prensibimiz.",
            },
            {
              title: "Yenilikçilik",
              description: "Sürekli gelişim ve yenilikçi çözümlerle sektörde öncü olmak.",
            },
            {
              title: "Kalite",
              description: "Her projede en yüksek standartları hedefleyen kalite odaklı yaklaşımımız.",
            },
            {
              title: "Şeffaflık",
              description: "Açık iletişim ve şeffaf süreçlerle müşteri memnuniyeti sağlamak.",
            },
          ],
        },
      },
      career: {
        hero: {
          title: "Kariyer",
          subtitle: "Dijital dönüşüm yolculuğunda bize katılın. Modern teknolojilerle çalışın, sürekli öğrenin ve kariyerinizi bir sonraki seviyeye taşıyın.",
        },
        whyJoin: {
          title: "Neden LAK VERSE?",
          subtitle: "Modern teknolojiler, yaratıcı projeler ve destekleyici bir ekip ortamı. Kariyerinizi bizimle birlikte şekillendirin.",
          items: [
            {
              title: "Kariyer Gelişimi",
              description: "Sürekli öğrenme ve gelişim fırsatları",
            },
            {
              title: "Sağlık & Refah",
              description: "Kapsamlı sağlık sigortası ve refah paketi",
            },
            {
              title: "Esnek Çalışma",
              description: "Uzaktan ve hibrit çalışma seçenekleri",
            },
            {
              title: "Takım Kültürü",
              description: "Destekleyici ve işbirlikçi ekip ortamı",
            },
          ],
        },
        openings: {
          title: "Açık Pozisyonlar",
          noOpenings: "Şu anda aktif bir ilanımız bulunmuyor.",
          generalApplication: {
            title: "Genel Başvuru",
            description: "Deneyiminize ve ilgi alanlarınıza uygun bir pozisyon açıldığında sizinle iletişime geçebilmemiz için genel başvuru formunu doldurun.",
            button: "Genel Başvuru Yap",
          },
        },
      },
      careerApplication: {
        hero: {
          title: "Kariyer Başvurusu",
          subtitle: "LAK VERSE ailesine katılmak için başvuru formunu doldurun. Yeteneklerinizi ve deneyimlerinizi paylaşın.",
        },
        sections: {
          personalInfo: "Kişisel Bilgiler",
          professionalInfo: "Profesyonel Bilgiler",
          resume: "Özgeçmiş",
          coverLetter: "Ön Yazı",
        },
        fields: {
          name: "Ad Soyad",
          email: "E-posta Adresi",
          phone: "Telefon Numarası",
          position: "Başvurduğunuz Pozisyon",
          selectPosition: "Pozisyon Seçiniz",
          experience: "Deneyim Süresi",
          selectExperience: "Deneyim Süresi Seçiniz",
          linkedin: "LinkedIn Profili",
          portfolio: "Portfolio / GitHub",
          resumeUpload: "CV / Özgeçmiş Yükle",
          resumePlaceholder: "PDF, DOC veya DOCX formatında dosya yükleyin",
          resumeMaxSize: "Maksimum 5MB",
          coverLetterLabel: "Neden LAK VERSE?",
          coverLetterPlaceholder: "Kendinizden bahsedin, neden bu pozisyona uygun olduğunuzu ve neden LAK VERSE'de çalışmak istediğinizi açıklayın...",
        },
        experienceOptions: {
          "0-1": "0-1 Yıl",
          "1-3": "1-3 Yıl",
          "3-5": "3-5 Yıl",
          "5-7": "5-7 Yıl",
          "7+": "7+ Yıl",
        },
        positionOptions: {
          seniorFrontend: "Senior Frontend Developer",
          uiUx: "UI/UX Designer",
          mobileApp: "Mobile App Developer",
          aiMl: "AI/ML Engineer",
          backend: "Backend Developer",
          fullstack: "Full Stack Developer",
          general: "Genel Başvuru",
        },
        buttons: {
          back: "← Kariyer sayfasına dön",
          submit: "Başvuruyu Gönder",
          submitting: "Gönderiliyor...",
        },
        messages: {
          resumeRequired: "Lütfen CV/özgeçmiş dosyanızı yükleyin.",
          submitError: "Başvurunuz gönderilemedi. Lütfen tekrar deneyin.",
          submitSuccess: "Başvurunuz alındı! En kısa sürede size dönüş yapacağız.",
        },
      },
      products: {
        title: "Ürünlerimiz | LAKVERSE - Geliştirme Aşamasındaki Ürünler",
        description: "LAKVERSE tarafından geliştirilen, abonelik sistemi ile kullanıcılara ulaştırılan uygulamalar. Maliyet takip, AI destekli hasta takip ve AI ile fotoğraf üretme uygulamaları.",
        hero: {
          title: "Ürünlerimiz",
          subtitle: "LAKVERSE tarafından geliştirilen, abonelik sistemi ile kullanıcılara ulaştırılan uygulamalar",
        },
        intro: {
          title: "Yakında Sizlerle",
          description: "LAKVERSE olarak, farklı sektörlerde ihtiyaç duyulan özel çözümler geliştiriyoruz. Bu ürünler, abonelik sistemi ile kullanıcılara ulaştırılan, bağımsız web uygulamalarıdır.",
        },
        comingSoon: "Yakında",
        subscriptionBased: "Abonelik Sistemi",
        items: [
          {
            title: "Maliyet Takip",
            description: "İşletmelerin maliyetlerini takip etmelerini ve analiz etmelerini sağlayan kapsamlı bir maliyet yönetim sistemi. Proje bazlı maliyet takibi, bütçe yönetimi ve detaylı raporlama özellikleri ile işletmenizin finansal kontrolünü sağlayın.",
            features: [
              "Proje bazlı maliyet takibi",
              "Bütçe yönetimi ve planlama",
              "Detaylı maliyet raporları",
              "Gider kategorileri ve analizleri",
              "Gelir-gider karşılaştırmaları",
            ],
          },
          {
            title: "AI Destekli Hasta Takip",
            description: "Sağlık sektörü için geliştirilmiş, yapay zeka destekli hasta takip ve yönetim sistemi. Hasta bilgilerinin güvenli şekilde saklanması, randevu yönetimi ve AI destekli ön tanı önerileri ile sağlık kuruluşlarının işleyişini optimize edin.",
            features: [
              "Güvenli hasta bilgi yönetimi",
              "Randevu takip ve yönetimi",
              "AI destekli ön tanı önerileri",
              "Hasta geçmişi ve kayıtları",
              "Doktor-hasta iletişim platformu",
            ],
          },
          {
            title: "AI ile Fotoğraf Üretme",
            description: "Yapay zeka teknolojileri kullanarak yüksek kaliteli görseller üreten, yaratıcı içerik oluşturma platformu. Metin tabanlı prompt'lardan profesyonel görseller oluşturun, mevcut görselleri düzenleyin ve yaratıcı projeleriniz için AI destekli görsel içerik üretin.",
            features: [
              "Metin tabanlı görsel üretimi",
              "Yüksek kaliteli AI görselleri",
              "Görsel düzenleme ve iyileştirme",
              "Çoklu stil ve tema seçenekleri",
              "Toplu görsel üretimi",
            ],
          },
        ],
      },
      quoteRequest: {
        hero: {
          title: "Teklif Al",
          subtitle: "Projeniz için detaylı teklif almak için formu doldurun. Uzman ekibimiz en kısa sürede size özel çözümler sunacak.",
          benefits: [
            { text: "24 Saat İçinde Yanıt" },
            { text: "Ücretsiz Teklif" },
            { text: "Hızlı Çözüm" },
            { text: "Uzman Destek" },
          ],
        },
        sections: {
          personalInfo: "Kişisel Bilgiler",
          projectDetails: "Proje Detayları",
          projectAbout: "Proje Hakkında",
        },
        fields: {
          name: "Ad Soyad",
          company: "Şirket Adı",
          email: "E-posta Adresi",
          phone: "Telefon Numarası",
          service: "Hizmet(ler)i Seçiniz",
          selectService: "Hizmet seçiniz...",
          deadline: "Hedef Teslimat Süresi",
          selectDeadline: "Teslimat süresi seçiniz",
          message: "Proje Detayları",
          messagePlaceholder: "Projeniz hakkında detaylı bilgi veriniz. Hedefleriniz, beklentileriniz ve özel gereksinimlerinizi paylaşabilirsiniz...",
        },
        buttons: {
          submit: "Teklif İste",
          submitting: "Gönderiliyor...",
        },
        messages: {
          error: "Bir hata oluştu. Lütfen tekrar deneyin.",
          success: "Teklif talebiniz alındı! En kısa sürede size dönüş yapacağız.",
        },
      },
      contact: {
        hero: {
          title: "İletişim",
          subtitle: "Projeleriniz için bizimle iletişime geçin",
        },
        form: {
          title: "Bize Ulaşın",
          subtitle: "Formu doldurarak bize ulaşabilir veya aşağıdaki iletişim bilgilerimizi kullanabilirsiniz.",
          fields: {
            name: "Ad Soyad",
            email: "E-posta",
            phone: "Telefon",
            subject: "Konu",
            message: "Mesaj",
          },
          button: "Gönder",
          submitting: "Gönderiliyor...",
        },
        info: {
          contactInfo: "İletişim Bilgileri",
          workingHours: "Çalışma Saatleri",
          followUs: "Bizi Takip Edin",
          hours: {
            weekdays: "Pazartesi - Cuma: 09:00 - 18:00",
            saturday: "Cumartesi: 10:00 - 16:00",
            sunday: "Pazar: Kapalı",
          },
        },
        messages: {
          error: "Mesaj gönderilemedi. Lütfen tekrar deneyin.",
          success: "Mesajınız gönderildi! En kısa sürede size dönüş yapacağız.",
        },
      },
      privacyPolicy: {
        title: "Gizlilik Politikası",
        description: "LAKVERSE gizlilik politikası ve kişisel verilerin korunması hakkında bilgiler.",
        lastUpdated: "Son güncelleme",
        sections: {
          general: {
            title: "1. Genel Bilgiler",
            paragraphs: [
              "LAKVERSE olarak, kişisel verilerinizin korunmasına büyük önem vermekteyiz. Bu Gizlilik Politikası, web sitemizi ziyaret ettiğinizde veya hizmetlerimizden yararlandığınızda toplanan kişisel verilerinizin nasıl toplandığını, kullanıldığını, korunduğunu ve paylaşıldığını açıklamaktadır.",
              "Bu politika, 6698 sayılı Kişisel Verilerin Korunması Kanunu (\"KVKK\") ve ilgili mevzuat hükümlerine uygun olarak hazırlanmıştır.",
            ],
          },
          dataController: {
            title: "2. Veri Sorumlusu",
            paragraphs: ["Kişisel verilerinizin işlenmesinden sorumlu olan veri sorumlusu:"],
            company: "LAKVERSE",
            email: "E-posta",
            web: "Web",
          },
          collectedData: {
            title: "3. Toplanan Kişisel Veriler",
            intro: "Hizmetlerimiz kapsamında aşağıdaki kişisel verileriniz toplanabilmektedir:",
            identity: {
              title: "3.1. Kimlik Bilgileri",
              items: ["Ad, soyad", "E-posta adresi", "Telefon numarası", "İş unvanı"],
            },
            contact: {
              title: "3.2. İletişim Bilgileri",
              items: ["E-posta adresi", "Telefon numarası", "Adres bilgileri"],
            },
            transaction: {
              title: "3.3. İşlem Bilgileri",
              items: ["Proje talepleri ve detayları", "Teklif formu bilgileri", "İletişim formu bilgileri"],
            },
            technical: {
              title: "3.4. Teknik Bilgiler",
              items: ["IP adresi", "Tarayıcı bilgileri", "Çerez (cookie) bilgileri", "Ziyaret edilen sayfalar", "Ziyaret tarih ve saati"],
            },
          },
          processingPurposes: {
            title: "4. Kişisel Verilerin İşlenme Amaçları",
            intro: "Toplanan kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:",
            items: [
              "Hizmet taleplerinizin değerlendirilmesi ve karşılanması",
              "Teklif hazırlama ve sunma",
              "Proje yönetimi ve takibi",
              "Müşteri ilişkileri yönetimi",
              "İletişim ve bilgilendirme faaliyetleri",
              "Yasal yükümlülüklerin yerine getirilmesi",
              "Web sitesi analizi ve iyileştirme",
              "Güvenlik ve dolandırıcılık önleme",
              "Pazarlama ve tanıtım faaliyetleri (açık rıza ile)",
            ],
          },
          legalBasis: {
            title: "5. Kişisel Verilerin İşlenme Hukuki Sebepleri",
            intro: "Kişisel verileriniz aşağıdaki hukuki sebeplerle işlenmektedir:",
            items: [
              "Açık rızanız",
              "Sözleşmenin kurulması veya ifası ile doğrudan ilgili olması",
              "Yasal yükümlülüğümüzün yerine getirilmesi",
              "Hukuki yükümlülüklerimizin yerine getirilmesi",
              "Meşru menfaatlerimiz (hizmet kalitesinin artırılması, güvenliğin sağlanması)",
            ],
          },
          dataSharing: {
            title: "6. Kişisel Verilerin Paylaşılması",
            paragraphs: [
              "Kişisel verileriniz, yukarıda belirtilen amaçların gerçekleştirilmesi için aşağıdaki üçüncü kişilerle paylaşılabilir:",
            ],
            items: [
              "Yasal yükümlülüklerimiz gereği kamu kurum ve kuruluşları",
              "Hizmet sağlayıcılarımız (hosting, e-posta servisleri, analitik araçlar)",
              "İş ortaklarımız ve tedarikçilerimiz",
              "Yasal danışmanlarımız ve muhasebe firmaları",
            ],
          },
          cookies: {
            title: "7. Çerezler (Cookies)",
            paragraphs: [
              "Web sitemizde, kullanıcı deneyimini iyileştirmek ve site performansını analiz etmek amacıyla çerezler kullanılmaktadır. Çerez kullanımı hakkında detaylı bilgi için KVKK Aydınlatma Metni sayfamızı ziyaret edebilirsiniz.",
            ],
          },
          retention: {
            title: "8. Kişisel Verilerin Saklanma Süresi",
            paragraphs: [
              "Kişisel verileriniz, işleme amacının gerektirdiği süre boyunca ve yasal saklama süreleri dahilinde saklanmaktadır. Bu süre sona erdiğinde, kişisel verileriniz yasalara uygun şekilde silinir, yok edilir veya anonim hale getirilir.",
            ],
          },
          security: {
            title: "9. Kişisel Verilerinizin Güvenliği",
            intro: "Kişisel verilerinizin güvenliğini sağlamak için teknik ve idari güvenlik önlemleri almaktayız:",
            items: [
              "SSL/TLS şifreleme teknolojileri",
              "Güvenli sunucu altyapısı",
              "Düzenli güvenlik güncellemeleri",
              "Erişim kontrolü ve yetkilendirme",
              "Düzenli yedekleme ve veri kurtarma prosedürleri",
            ],
          },
          rights: {
            title: "10. Haklarınız",
            intro: "KVKK kapsamında aşağıdaki haklara sahipsiniz:",
            items: [
              "Kişisel verilerinizin işlenip işlenmediğini öğrenme",
              "İşlenmişse buna ilişkin bilgi talep etme",
              "İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme",
              "Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme",
              "Eksik veya yanlış işlenmişse düzeltilmesini isteme",
              "KVKK'da öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme",
              "Düzeltme, silme, yok edilme işlemlerinin, kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme",
              "İşlenen verilerin münhasıran otomatik sistemler ile analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme",
              "Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın giderilmesini talep etme",
            ],
          },
          application: {
            title: "11. Başvuru Yöntemi",
            paragraphs: [
              "Yukarıda belirtilen haklarınızı kullanmak için, kimliğinizi tespit edici belgelerle birlikte info@lakverse.com adresine e-posta gönderebilir veya yazılı olarak başvurabilirsiniz.",
              "Başvurunuz, talebinizin niteliğine göre en geç 30 (otuz) gün içinde sonuçlandırılacaktır.",
            ],
          },
          changes: {
            title: "12. Değişiklikler",
            paragraphs: [
              "Bu Gizlilik Politikası, yasal düzenlemelerdeki değişiklikler veya iş süreçlerimizdeki güncellemeler nedeniyle değiştirilebilir. Önemli değişiklikler web sitemizde duyurulacaktır.",
            ],
          },
          contact: {
            title: "13. İletişim",
            paragraphs: ["Gizlilik politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:"],
            company: "LAKVERSE",
            email: "E-posta",
            web: "Web",
          },
        },
      },
      cookiePolicy: {
        title: "Çerez Politikası",
        description: "LAKVERSE çerez (cookie) kullanım politikası ve çerez tercihleri hakkında bilgiler.",
        lastUpdated: "Son güncelleme",
        sections: {
          whatIs: {
            title: "1. Çerez Nedir?",
            paragraphs: [
              "Çerezler (cookies), web sitelerini ziyaret ettiğinizde cihazınıza (bilgisayar, tablet, telefon) kaydedilen küçük metin dosyalarıdır. Çerezler, web sitesinin düzgün çalışmasını sağlar, kullanıcı deneyimini iyileştirir ve site performansını analiz etmemize yardımcı olur.",
            ],
          },
          purpose: {
            title: "2. Çerezlerin Kullanım Amacı",
            intro: "Web sitemizde çerezler aşağıdaki amaçlarla kullanılmaktadır:",
            items: [
              "Web sitesinin temel işlevlerinin çalışmasını sağlamak",
              "Kullanıcı tercihlerini hatırlamak",
              "Site performansını analiz etmek ve iyileştirmek",
              "Kullanıcı deneyimini kişiselleştirmek",
              "Güvenliği sağlamak",
              "Pazarlama ve reklam faaliyetleri (açık rıza ile)",
            ],
          },
          types: {
            title: "3. Kullandığımız Çerez Türleri",
            required: {
              title: "3.1. Zorunlu Çerezler",
              paragraphs: [
                "Bu çerezler, web sitesinin temel işlevlerinin çalışması için zorunludur. Bu çerezler olmadan web sitesi düzgün çalışmaz. Bu çerezler için kullanıcı rızası alınmaz çünkü web sitesinin çalışması için gereklidir.",
              ],
              examples: {
                title: "Örnekler:",
                items: ["Oturum yönetimi çerezleri", "Güvenlik çerezleri", "Dil tercihi çerezleri"],
              },
            },
            analytical: {
              title: "3.2. Analitik Çerezler",
              paragraphs: [
                "Bu çerezler, web sitesinin nasıl kullanıldığını anlamamıza yardımcı olur. Ziyaretçi sayısı, en çok ziyaret edilen sayfalar, kullanıcı davranışları gibi bilgileri toplar. Bu bilgiler anonimdir ve kişisel kimlik bilgilerinizi içermez.",
              ],
              tools: {
                title: "Kullandığımız Analitik Araçlar:",
                items: ["Google Analytics (anonim IP adresi ile)", "Web sitesi performans analizi araçları"],
              },
            },
            functional: {
              title: "3.3. Fonksiyonel Çerezler",
              paragraphs: [
                "Bu çerezler, web sitesinin gelişmiş özelliklerini ve kişiselleştirmeyi sağlar. Tercihlerinizi hatırlar ve size daha iyi bir deneyim sunar.",
              ],
              examples: {
                title: "Örnekler:",
                items: ["Dil tercihi", "Bölge ayarları", "Kullanıcı tercihleri"],
              },
            },
            marketing: {
              title: "3.4. Pazarlama Çerezleri",
              paragraphs: [
                "Bu çerezler, size daha uygun reklamlar ve içerikler göstermek için kullanılır. Bu çerezler sadece açık rızanız ile kullanılır.",
              ],
              examples: {
                title: "Örnekler:",
                items: ["Reklam kişiselleştirme", "İçerik önerileri", "Sosyal medya entegrasyonları"],
              },
            },
          },
          thirdParty: {
            title: "4. Üçüncü Taraf Çerezler",
            paragraphs: [
              "Web sitemizde, hizmet sağlayıcılarımız tarafından yerleştirilen üçüncü taraf çerezler de kullanılmaktadır. Bu çerezler, analitik, pazarlama ve diğer hizmetler için kullanılır.",
            ],
            services: {
              title: "Üçüncü Taraf Hizmetler:",
              items: ["Google Analytics", "Sosyal medya platformları (LinkedIn, vb.)", "Hosting ve altyapı sağlayıcıları"],
            },
          },
          retention: {
            title: "5. Çerezlerin Saklanma Süresi",
            intro: "Çerezler, kullanım amaçlarına göre farklı sürelerde saklanır:",
            items: [
              { type: "Oturum Çerezleri", duration: "Tarayıcı kapatıldığında silinir" },
              { type: "Kalıcı Çerezler", duration: "Belirli bir süre (genellikle 1-2 yıl) cihazınızda kalır" },
              { type: "Analitik Çerezler", duration: "Genellikle 2 yıl süreyle saklanır" },
              { type: "Pazarlama Çerezleri", duration: "Açık rızanız geri alınana kadar veya belirli bir süre (genellikle 1 yıl)" },
            ],
          },
          management: {
            title: "6. Çerez Tercihlerinizi Yönetme",
            paragraphs: [
              "Çerez tercihlerinizi tarayıcı ayarlarınızdan yönetebilirsiniz. Ancak, bazı çerezlerin devre dışı bırakılması web sitesinin bazı özelliklerinin çalışmamasına neden olabilir.",
            ],
            browserSettings: {
              title: "6.1. Tarayıcı Ayarları",
              intro: "Popüler tarayıcılarda çerez ayarlarını yönetmek için:",
              browsers: [
                { name: "Google Chrome", instructions: "Ayarlar → Gizlilik ve güvenlik → Çerezler ve diğer site verileri" },
                { name: "Mozilla Firefox", instructions: "Seçenekler → Gizlilik ve Güvenlik → Çerezler ve site verileri" },
                { name: "Safari", instructions: "Tercihler → Gizlilik → Çerezler ve web sitesi verileri" },
                { name: "Microsoft Edge", instructions: "Ayarlar → Gizlilik, arama ve hizmetler → Çerezler ve site izinleri" },
              ],
            },
            tools: {
              title: "6.2. Çerez Yönetim Araçları",
              paragraphs: [
                "Web sitemizde, çerez tercihlerinizi yönetmek için bir çerez yönetim paneli sunulmaktadır. İlk ziyaretinizde veya tarayıcı ayarlarınızdan çerez tercihlerinizi değiştirebilirsiniz.",
              ],
            },
          },
          disabling: {
            title: "7. Çerezlerin Devre Dışı Bırakılmasının Etkileri",
            intro: "Çerezleri devre dışı bırakırsanız, web sitesinin bazı özellikleri düzgün çalışmayabilir:",
            items: [
              "Oturum yönetimi sorunları",
              "Tercihlerinizin kaydedilmemesi",
              "Kişiselleştirilmiş içeriklerin gösterilmemesi",
              "Form verilerinin kaybolması",
              "Site performansının düşmesi",
            ],
          },
          mobile: {
            title: "8. Mobil Cihazlarda Çerez Yönetimi",
            intro: "Mobil cihazlarda da çerez tercihlerinizi yönetebilirsiniz:",
            items: [
              { platform: "iOS (Safari)", instructions: "Ayarlar → Safari → Gizlilik ve Güvenlik → Çerezleri Engelle" },
              { platform: "Android (Chrome)", instructions: "Chrome menü → Ayarlar → Site ayarları → Çerezler" },
            ],
          },
          changes: {
            title: "9. Çerez Politikası Değişiklikleri",
            paragraphs: [
              "Bu Çerez Politikası, yasal düzenlemelerdeki değişiklikler veya web sitesi güncellemeleri nedeniyle değiştirilebilir. Önemli değişiklikler web sitemizde duyurulacaktır.",
            ],
          },
          contact: {
            title: "10. İletişim",
            paragraphs: ["Çerez politikamız hakkında sorularınız için bizimle iletişime geçebilirsiniz:"],
            company: "LAKVERSE",
            email: "E-posta",
            web: "Web",
          },
        },
      },
      termsOfUse: {
        title: "Kullanım Koşulları",
        description: "LAKVERSE kullanım koşulları ve hizmet şartları hakkında bilgiler.",
        lastUpdated: "Son güncelleme",
        sections: {
          general: {
            title: "1. Genel Hükümler",
            paragraphs: [
              "Bu Kullanım Koşulları, LAKVERSE web sitesi ve hizmetlerinin kullanımına ilişkin kuralları belirlemektedir. Web sitemizi ziyaret ederek veya hizmetlerimizden yararlanarak, bu koşulları kabul etmiş sayılırsınız.",
              "LAKVERSE olarak, bu koşulları önceden haber vermeksizin değiştirme hakkını saklı tutarız. Değişiklikler, web sitemizde yayınlandığı tarihten itibaren geçerli olacaktır.",
            ],
          },
          services: {
            title: "2. Hizmetlerin Tanımı",
            intro: "LAKVERSE, aşağıdaki hizmetleri sunmaktadır:",
            items: [
              "Web tasarım ve geliştirme hizmetleri",
              "Mobil uygulama geliştirme",
              "E-ticaret çözümleri",
              "Yazılım geliştirme ve danışmanlık",
              "AI destekli otomasyon sistemleri",
              "Dijital dönüşüm danışmanlığı",
            ],
          },
          obligations: {
            title: "3. Kullanıcı Yükümlülükleri",
            intro: "Web sitemizi ve hizmetlerimizi kullanırken aşağıdaki kurallara uymanız gerekmektedir:",
            items: [
              "Yasalara ve düzenlemelere uygun davranmak",
              "Başkalarının haklarını ihlal etmemek",
              "Telif hakkı, marka veya diğer fikri mülkiyet haklarını ihlal etmemek",
              "Zararlı yazılım, virüs veya kötü amaçlı kod yüklememek",
              "Hizmetlerimizi kötüye kullanmamak veya sistem güvenliğini tehdit etmemek",
              "Yanlış, yanıltıcı veya yasadışı bilgi paylaşmamak",
            ],
          },
          intellectualProperty: {
            title: "4. Fikri Mülkiyet Hakları",
            paragraphs: [
              "Web sitemizdeki tüm içerikler (metinler, görseller, logolar, tasarımlar, yazılımlar vb.) LAKVERSE'e aittir ve telif hakkı, marka ve diğer fikri mülkiyet yasaları ile korunmaktadır.",
              "İçeriklerimizi izinsiz kopyalama, çoğaltma, dağıtma veya ticari amaçlarla kullanma yasaktır. Ancak, kişisel ve ticari olmayan amaçlarla sınırlı olmak kaydıyla, içeriklerimizi paylaşabilirsiniz.",
            ],
          },
          contracts: {
            title: "5. Hizmet Sözleşmeleri",
            paragraphs: [
              "LAKVERSE ile yapılacak hizmet sözleşmeleri, ayrı bir sözleşme metni ile düzenlenir. Bu kullanım koşulları, genel hükümler olup, özel sözleşmelerde belirtilen koşullar önceliklidir.",
              "Hizmet bedelleri, ödeme koşulları, teslimat süreleri ve diğer detaylar, her proje için ayrıca belirlenir ve müşteriye yazılı olarak bildirilir.",
            ],
          },
          warranty: {
            title: "6. Garanti ve Sorumluluk Sınırlaması",
            paragraphs: [
              "LAKVERSE, hizmetlerini mevcut teknoloji ve bilgi birikimi çerçevesinde en iyi şekilde sunmaya çalışır. Ancak, hizmetlerimizin kesintisiz, hatasız veya güvenli olacağına dair garanti vermez.",
              "LAKVERSE, web sitesi veya hizmetlerin kullanımından kaynaklanan doğrudan, dolaylı, özel veya sonuç olarak ortaya çıkan zararlardan sorumlu tutulamaz.",
            ],
          },
          thirdParty: {
            title: "7. Üçüncü Taraf Bağlantıları",
            paragraphs: [
              "Web sitemizde, üçüncü taraf web sitelerine bağlantılar bulunabilir. Bu bağlantılar, yalnızca bilgilendirme amaçlıdır ve LAKVERSE, bu sitelerin içeriğinden veya gizlilik uygulamalarından sorumlu değildir.",
            ],
          },
          dataSecurity: {
            title: "8. Veri Güvenliği",
            paragraphs: [
              "LAKVERSE, kişisel verilerinizin güvenliğini sağlamak için teknik ve idari önlemler alır. Ancak, internet üzerinden veri aktarımının tam güvenliğini garanti edemeyiz.",
              "Kişisel verilerinizin işlenmesi hakkında detaylı bilgi için Gizlilik Politikamızı inceleyebilirsiniz.",
            ],
          },
          cancellation: {
            title: "9. İptal ve İade",
            paragraphs: [
              "Hizmet sözleşmelerinin iptali ve iade koşulları, her proje için ayrıca belirlenir ve sözleşme metninde yer alır. Genel olarak, başlanmış projeler için iade yapılmaz.",
            ],
          },
          changes: {
            title: "10. Değişiklikler ve Güncellemeler",
            paragraphs: [
              "LAKVERSE, bu kullanım koşullarını herhangi bir zamanda değiştirme hakkını saklı tutar. Önemli değişiklikler, web sitemizde duyurulacaktır.",
              "Değişikliklerden haberdar olmak için bu sayfayı düzenli olarak ziyaret etmenizi öneririz.",
            ],
          },
          law: {
            title: "11. Uygulanacak Hukuk ve Yetki",
            paragraphs: [
              "Bu kullanım koşulları, Türkiye Cumhuriyeti yasalarına tabidir. Bu koşullardan kaynaklanan uyuşmazlıklar, Türkiye Cumhuriyeti mahkemelerinde çözülecektir.",
            ],
          },
          contact: {
            title: "12. İletişim",
            paragraphs: ["Kullanım koşulları ile ilgili sorularınız için bizimle iletişime geçebilirsiniz:"],
            company: "LAKVERSE",
            email: "E-posta",
            phone: "Telefon",
            web: "Web",
          },
        },
      },
      kvkk: {
        title: "KVKK Aydınlatma Metni",
        description: "LAKVERSE Kişisel Verilerin Korunması Kanunu (KVKK) aydınlatma metni.",
        subtitle: "6698 sayılı Kişisel Verilerin Korunması Kanunu uyarınca hazırlanmıştır.",
        lastUpdated: "Son güncelleme",
        sections: {
          dataController: {
            title: "1. Veri Sorumlusu",
            paragraphs: [
              "6698 sayılı Kişisel Verilerin Korunması Kanunu (\"KVKK\") uyarınca, kişisel verileriniz aşağıda belirtilen çerçevede işlenecektir.",
            ],
            company: "LAKVERSE",
            email: "E-posta",
            web: "Web",
          },
          processedData: {
            title: "2. İşlenen Kişisel Veriler",
            intro: "Hizmetlerimiz kapsamında, aşağıdaki kategorilerdeki kişisel verileriniz işlenmektedir:",
            identity: {
              title: "2.1. Kimlik Verileri",
              items: ["Ad, soyad", "TC Kimlik numarası (yasal zorunluluklar gereği)", "Doğum tarihi"],
            },
            contact: {
              title: "2.2. İletişim Verileri",
              items: ["E-posta adresi", "Telefon numarası", "Adres bilgileri", "Faks numarası"],
            },
            customer: {
              title: "2.3. Müşteri İşlem Verileri",
              items: ["Proje talepleri", "Teklif bilgileri", "Sözleşme bilgileri", "Fatura bilgileri", "Ödeme bilgileri"],
            },
            marketing: {
              title: "2.4. Pazarlama Verileri",
              items: ["Pazarlama ve iletişim tercihleri", "Kampanya bilgileri", "Abonelik bilgileri"],
            },
            security: {
              title: "2.5. İşlem Güvenliği Verileri",
              items: ["IP adresi", "Çerez (cookie) bilgileri", "Log kayıtları", "Tarayıcı bilgileri"],
            },
            media: {
              title: "2.6. Görsel ve İşitsel Kayıtlar",
              items: ["Fotoğraf (proje görselleri, etkinlik fotoğrafları)", "Video kayıtları (proje sunumları)"],
            },
          },
          processingPurposes: {
            title: "3. Kişisel Verilerin İşlenme Amaçları",
            intro: "Kişisel verileriniz aşağıdaki amaçlarla işlenmektedir:",
            items: [
              "Hizmet taleplerinizin değerlendirilmesi ve karşılanması",
              "Teklif hazırlama, sunma ve sözleşme süreçlerinin yürütülmesi",
              "Proje yönetimi, takibi ve teslimi",
              "Müşteri ilişkileri yönetimi ve müşteri memnuniyetinin artırılması",
              "İletişim faaliyetlerinin yürütülmesi",
              "Fatura, fatura düzenleme ve muhasebe işlemlerinin yürütülmesi",
              "Yasal yükümlülüklerin yerine getirilmesi",
              "Hukuki uyuşmazlıkların çözümü",
              "Web sitesi analizi ve kullanıcı deneyiminin iyileştirilmesi",
              "Güvenlik ve dolandırıcılık önleme faaliyetleri",
              "Pazarlama ve tanıtım faaliyetleri (açık rıza ile)",
              "E-bülten ve kampanya gönderimi (açık rıza ile)",
            ],
          },
          legalBasis: {
            title: "4. Kişisel Verilerin İşlenmesinin Hukuki Sebepleri",
            intro: "Kişisel verileriniz aşağıdaki hukuki sebeplerle işlenmektedir:",
            items: [
              { law: "KVKK md. 5/2-a", description: "Açık rızanızın bulunması" },
              { law: "KVKK md. 5/2-c", description: "Sözleşmenin kurulması veya ifası ile doğrudan ilgili olması" },
              { law: "KVKK md. 5/2-e", description: "Veri sorumlusunun hukuki yükümlülüğünü yerine getirebilmesi için zorunlu olması" },
              { law: "KVKK md. 5/2-f", description: "İşlenen verilerin ilgili kişi tarafından alenileştirilmiş olması" },
              { law: "KVKK md. 5/2-ç", description: "Bir hakkın tesisi, kullanılması veya korunması için veri işlemenin zorunlu olması" },
              { law: "KVKK md. 5/2-f", description: "Meşru menfaatlerimiz için veri işlemenin zorunlu olması" },
            ],
          },
          dataTransfer: {
            title: "5. Kişisel Verilerin Aktarılması",
            paragraphs: [
              "Kişisel verileriniz, yukarıda belirtilen amaçların gerçekleştirilmesi için aşağıdaki üçüncü kişilere aktarılabilir:",
            ],
            domestic: {
              title: "5.1. Yurt İçi Aktarımlar",
              items: [
                "Yasal yükümlülüklerimiz gereği kamu kurum ve kuruluşları",
                "Hizmet sağlayıcılarımız (hosting, bulut depolama, e-posta servisleri)",
                "Analitik ve pazarlama araçları sağlayıcıları",
                "Ödeme işlem şirketleri",
                "Yasal danışmanlarımız ve muhasebe firmaları",
                "İş ortaklarımız ve tedarikçilerimiz",
              ],
            },
            international: {
              title: "5.2. Yurt Dışı Aktarımlar",
              paragraphs: [
                "Kişisel verileriniz, hizmet sağlayıcılarımızın bulunduğu yurt dışı ülkelere aktarılabilir. Bu aktarımlar, KVKK md. 9 hükümleri ve Kişisel Verileri Koruma Kurulu kararları çerçevesinde, yeterli korumanın bulunduğu ülkelere veya yeterli korumanın bulunmadığı ülkelerde yazılı taahhütname ile yapılmaktadır.",
              ],
            },
          },
          collection: {
            title: "6. Kişisel Verilerin Toplanma Yöntemi",
            intro: "Kişisel verileriniz aşağıdaki yöntemlerle toplanmaktadır:",
            items: [
              "Web sitemizdeki iletişim formları",
              "Teklif talep formları",
              "E-posta iletişimi",
              "Telefon görüşmeleri",
              "Yüz yüze görüşmeler",
              "Sosyal medya platformları",
              "Çerezler (cookies) ve benzeri teknolojiler",
              "Otomatik olarak toplanan teknik veriler (log dosyaları, IP adresi vb.)",
            ],
          },
          cookies: {
            title: "7. Çerezler (Cookies)",
            paragraphs: [
              "Web sitemizde, kullanıcı deneyimini iyileştirmek, site performansını analiz etmek ve kişiselleştirilmiş içerik sunmak amacıyla çerezler kullanılmaktadır.",
            ],
            types: {
              title: "7.1. Kullanılan Çerez Türleri",
              items: [
                { type: "Zorunlu Çerezler", description: "Web sitesinin temel işlevlerinin çalışması için gerekli çerezler" },
                { type: "Analitik Çerezler", description: "Site kullanımını analiz etmek için kullanılan çerezler" },
                { type: "Fonksiyonel Çerezler", description: "Kullanıcı tercihlerini hatırlamak için kullanılan çerezler" },
                { type: "Pazarlama Çerezleri", description: "Kişiselleştirilmiş reklamlar için kullanılan çerezler (açık rıza ile)" },
              ],
            },
            management: {
              title: "Çerez Tercihlerinizi Yönetme",
              paragraphs: [
                "Çerez tercihlerinizi tarayıcı ayarlarınızdan yönetebilirsiniz. Ancak, bazı çerezlerin devre dışı bırakılması web sitesinin bazı özelliklerinin çalışmamasına neden olabilir.",
              ],
            },
          },
          retention: {
            title: "8. Kişisel Verilerin Saklanma Süresi",
            intro: "Kişisel verileriniz, işleme amacının gerektirdiği süre boyunca ve yasal saklama süreleri dahilinde saklanmaktadır:",
            items: [
              { type: "Müşteri verileri", duration: "Sözleşme süresi + 10 yıl (Vergi Usul Kanunu gereği)" },
              { type: "İletişim verileri", duration: "İletişim sona erene kadar + 2 yıl" },
              { type: "Pazarlama verileri", duration: "Açık rızanız geri alınana kadar" },
              { type: "Teknik veriler", duration: "2 yıl" },
              { type: "Yasal saklama süreleri", duration: "İlgili mevzuat hükümlerine göre" },
            ],
            note: "Bu süreler sona erdiğinde, kişisel verileriniz yasalara uygun şekilde silinir, yok edilir veya anonim hale getirilir.",
          },
          security: {
            title: "9. Kişisel Verilerin Güvenliği",
            intro: "Kişisel verilerinizin güvenliğini sağlamak için aşağıdaki teknik ve idari güvenlik önlemleri alınmaktadır:",
            items: [
              "SSL/TLS şifreleme teknolojileri ile veri iletimi",
              "Güvenli sunucu altyapısı ve veri merkezleri",
              "Düzenli güvenlik güncellemeleri ve yamalar",
              "Erişim kontrolü ve yetkilendirme mekanizmaları",
              "Düzenli yedekleme ve veri kurtarma prosedürleri",
              "Güvenlik duvarları ve saldırı tespit sistemleri",
              "Personel eğitimleri ve bilinçlendirme faaliyetleri",
              "Fiziksel güvenlik önlemleri",
            ],
          },
          rights: {
            title: "10. İlgili Kişinin Hakları (KVKK md. 11)",
            intro: "KVKK md. 11 uyarınca, aşağıdaki haklara sahipsiniz:",
            items: [
              { title: "Bilgi talep etme", description: "Kişisel verilerinizin işlenip işlenmediğini öğrenme, işlenmişse buna ilişkin bilgi talep etme" },
              { title: "İşleme amacını öğrenme", description: "İşlenme amacını ve bunların amacına uygun kullanılıp kullanılmadığını öğrenme" },
              { title: "Yurt içi/yurt dışı aktarım bilgisi", description: "Yurt içinde veya yurt dışında aktarıldığı üçüncü kişileri bilme" },
              { title: "Düzeltme talep etme", description: "Eksik veya yanlış işlenmişse düzeltilmesini isteme" },
              { title: "Silme/yok etme talep etme", description: "KVKK md. 7'de öngörülen şartlar çerçevesinde silinmesini veya yok edilmesini isteme" },
              { title: "Üçüncü kişilere bildirim", description: "Düzeltme, silme, yok edilme işlemlerinin, kişisel verilerin aktarıldığı üçüncü kişilere bildirilmesini isteme" },
              { title: "İtiraz etme", description: "İşlenen verilerin münhasıran otomatik sistemler ile analiz edilmesi suretiyle kişinin kendisi aleyhine bir sonucun ortaya çıkmasına itiraz etme" },
              { title: "Zararın giderilmesini talep etme", description: "Kişisel verilerin kanuna aykırı olarak işlenmesi sebebiyle zarara uğraması halinde zararın giderilmesini talep etme" },
            ],
          },
          application: {
            title: "11. Başvuru Yöntemi",
            paragraphs: [
              "Yukarıda belirtilen haklarınızı kullanmak için, kimliğinizi tespit edici belgelerle birlikte aşağıdaki yöntemlerle başvurabilirsiniz:",
            ],
            email: {
              title: "E-posta ile Başvuru:",
              address: "info@lakverse.com",
            },
            written: {
              title: "Yazılı Başvuru:",
              address: "LAKVERSE\nWorkinton Carousel, Fişekhane Cad. No: 5, Carousel AVM 2B Katı, 34710 Bakırköy / İstanbul",
            },
            note: "Başvurunuzda, talep ettiğiniz hak ve konuya ilişkin açıklamalarınızı belirtmeniz gerekmektedir. Başvurunuz, talebinizin niteliğine göre en geç 30 (otuz) gün içinde sonuçlandırılacaktır.",
            complaint: "Başvurunuzun reddedilmesi, verilen cevabın yetersiz bulunması veya süresinde başvuruya cevap verilmemesi halinde, KVKK md. 14 uyarınca Kişisel Verileri Koruma Kurulu'na şikayette bulunabilirsiniz.",
          },
          changes: {
            title: "12. Değişiklikler",
            paragraphs: [
              "Bu Aydınlatma Metni, yasal düzenlemelerdeki değişiklikler, iş süreçlerimizdeki güncellemeler veya Kişisel Verileri Koruma Kurulu'nun yeni kararları nedeniyle değiştirilebilir. Önemli değişiklikler web sitemizde duyurulacaktır.",
            ],
          },
          contact: {
            title: "13. İletişim",
            paragraphs: [
              "KVKK kapsamındaki haklarınız ve kişisel verilerinizin işlenmesi hakkında sorularınız için bizimle iletişime geçebilirsiniz:",
            ],
            company: "LAKVERSE",
            email: "E-posta",
            web: "Web",
          },
        },
      },
      services: {
        common: {
          features: "Özellikler",
          process: {
            title: "Kanıtlanmış Çalışma Sürecimiz",
            subtitle: "",
          },
          steps: [
            {
              title: "Teklif Al",
              description: "Projeniz için ücretsiz teklif alın ve ihtiyaçlarınızı paylaşın",
            },
            {
              title: "Planla",
              description: "Proje planını birlikte oluşturalım ve zaman çizelgesini belirleyelim",
            },
            {
              title: "Geliştir",
              description: "Profesyonel ekibimiz projenizi geliştirirken sizi bilgilendiriyoruz",
            },
            {
              title: "Yayınla",
              description: "Projeniz hazır! Yayınlıyoruz ve destek sağlıyoruz",
            },
          ],
        },
        webDesign: {
          title: "Web Tasarım Hizmetleri | Profesyonel Web Sitesi Tasarımı | LAK VERSE",
          description: "Modern, kullanıcı dostu ve Google'da üst sıralarda görünen web tasarımları. Telefon, tablet ve bilgisayarda mükemmel görünen, hızlı açılan web siteleri tasarlıyoruz.",
          hero: {
            title: "Web Tasarım",
            subtitle: "Modern, kullanıcı dostu ve Google'da üst sıralarda görünen web tasarımları ile markanızı dijital dünyada öne çıkarın",
          },
          features: [
            "Modern ve kullanıcı dostu tasarımlar",
            "Google'da üst sıralarda görünme",
            "Telefon, tablet ve bilgisayarda mükemmel görünüm",
            "Saniyeler içinde açılan hızlı sayfalar",
            "Markanıza özel, benzersiz tasarım çözümleri",
            "Müşterilerinizin kolayca kullanabileceği arayüzler",
          ],
          content: {
            title: "Profesyonel Web Tasarım Hizmetleri",
            paragraphs: [
              "Markanızın dijital kimliğini yansıtan, satışlarınızı artıran profesyonel web siteleri tasarlıyoruz. Modern tasarım prensipleri ve müşteri odaklı yaklaşımımızla, ziyaretçilerinizi etkileyecek ve iş hedeflerinize ulaşmanızı sağlayacak web siteleri oluşturuyoruz.",
              "Her projede, markanızın benzersiz değerlerini ve müşterilerinizin ihtiyaçlarını anlayarak, özel tasarım çözümleri geliştiriyoruz. Google'da üst sıralarda görünme, telefon ve tablette mükemmel görünüm ve hızlı açılma gibi özellikleri de düşünerek, hem görsel hem de kullanım açısından mükemmel web siteleri tasarlıyoruz.",
              "Web tasarım sürecimiz, markanızın müşterilerini anlamakla başlar. Müşteri araştırmaları, rakip analizleri ve sektör trendlerini inceleyerek, markanıza özel bir tasarım stratejisi geliştiriyoruz. Tüm cihazlarda mükemmel görünen, hızlı açılan ve müşterilerinizin kolayca kullanabileceği web siteleri oluşturuyoruz. Google'da üst sıralarda görünmeniz için gerekli teknik altyapıyı da tasarım aşamasında düşünerek, hem görsel hem de performans açısından üstün web siteleri tasarlıyoruz.",
            ],
          },
        },
        webDevelopment: {
          title: "Web Yazılım Hizmetleri | Özel Web Yazılım ve Uygulama Geliştirme | LAK VERSE",
          description: "İşletmenize özel web yazılımları: yönetim panelleri, müşteri portalları, entegrasyonlar ve ölçeklenebilir web uygulamaları. Hızlı, güvenli ve sürdürülebilir çözümler geliştiriyoruz.",
          hero: {
            title: "Web Yazılım",
            subtitle: "İşletmenize özel, hızlı, güvenli ve ölçeklenebilir web yazılımları geliştiriyoruz",
          },
          features: [
            "İşletmenize özel web yazılımları ve uygulamalar",
            "Yönetim panelleri ve müşteri portalları",
            "API geliştirme ve sistem entegrasyonları",
            "Güvenli, ölçeklenebilir ve bakımı kolay altyapı",
            "Yüksek performans ve SEO uyumlu yapı",
            "Mobil ve masaüstünde kusursuz çalışma",
          ],
          content: {
            title: "Profesyonel Web Yazılım Hizmetleri",
            paragraphs: [
              "İşletmenizin iş yapış şekline uyan, hızlı, güvenli ve kullanıcı dostu web yazılımları geliştiriyoruz. Hazır paketlerin yetmediği noktada; yönetim panelleri, müşteri portalları, rezervasyon ve sipariş sistemleri gibi tamamen size özel çözümler üretiyoruz.",
              "Yazılımın arka planında çalışan sistemlerden diğer platformlarla kurduğu bağlantılara kadar her detayı planlıyoruz. API geliştirme ve entegrasyonlarla kullandığınız araçları tek bir yapıda buluşturuyor, verilerinizin güvenliğini ve uygulamanızın performansını en baştan önceliklendiriyoruz.",
              "Modern teknolojilerle geliştirdiğimiz web yazılımları büyüdükçe sorun çıkarmayan, bakımı kolay ve güncel kalabilen bir mimariye sahip. Yayına aldıktan sonra da izleme, iyileştirme ve yeni özellik geliştirme süreçlerinde yanınızda oluyoruz.",
            ],
          },
        },
        mobileApp: {
          title: "Mobil Uygulama Geliştirme | iPhone ve Android Uygulamaları | LAK VERSE",
          description: "iPhone ve Android telefonlar için hızlı ve kullanıcı dostu mobil uygulamalar. Müşterilerinizin kolayca kullanabileceği, güvenilir mobil çözümler sunuyoruz.",
          hero: {
            title: "Mobil Uygulama",
            subtitle: "iPhone ve Android uygulamaları ile müşterilerinize her zaman, her yerde ulaşın",
          },
          features: [
            "iPhone ve Android telefonlar için uygulamalar",
            "Her iki platform için tek uygulama seçeneği",
            "Kolay kullanılabilir ve şık tasarım",
            "Hızlı ve sorunsuz çalışma",
            "App Store ve Google Play'de yayınlama desteği",
            "Diğer sistemlerle uyumlu bağlantılar",
          ],
          content: {
            title: "Profesyonel Mobil Uygulama Geliştirme",
            paragraphs: [
              "Müşterilerinizin kolayca kullanabileceği, hızlı ve güvenilir mobil uygulamalar geliştiriyoruz. iPhone ve Android telefonlar için özel uygulamalar veya her iki platform için tek bir uygulama seçeneği sunuyoruz. İşletmenizin ihtiyaçlarına en uygun çözümü birlikte belirliyoruz.",
              "Her projede, platform özelliklerini ve müşteri beklentilerini anlayarak, özel mobil uygulama çözümleri geliştiriyoruz. Her platform için özel uygulama veya tek uygulamayla her iki platformda çalışan çözümler arasından, projenizin ihtiyaçlarına en uygun seçeneği belirliyoruz.",
              "Mobil uygulama geliştirme sürecimiz, müşteri araştırmaları ve platform analizleriyle başlar. Modern teknolojiler kullanarak, hızlı ve kullanıcı dostu mobil uygulamalar geliştiriyoruz. App Store ve Google Play'de yayınlama süreçlerinde de destek sağlayarak, uygulamanızın başarılı bir şekilde yayınlanmasını sağlıyoruz.",
            ],
          },
        },
        desktopApp: {
          title: "Masaüstü Uygulama Geliştirme | Windows, Mac ve Linux Uygulamaları | LAK VERSE",
          description: "Windows, Mac ve Linux için modern ve hızlı masaüstü uygulamaları. İşletmenizin ihtiyaçlarına özel, güvenilir masaüstü çözümleri geliştiriyoruz.",
          hero: {
            title: "Masaüstü Uygulama",
            subtitle: "Windows, Mac ve Linux için işletmenizin ihtiyaçlarına özel uygulamalar",
          },
          features: [
            "Windows, Mac ve Linux için uygulamalar",
            "Tüm işletim sistemlerinde çalışan tek uygulama",
            "Modern ve kolay kullanılabilir arayüzler",
            "Hızlı çalışma ve güvenlik",
            "İnternet olmadan da çalışabilme",
            "Diğer sistemlerle uyumlu bağlantılar",
          ],
          content: {
            title: "Profesyonel Masaüstü Uygulama Geliştirme",
            paragraphs: [
              "Windows, Mac ve Linux için modern ve hızlı masaüstü uygulamaları geliştiriyoruz. İşletmenizin ihtiyaçlarına özel, güvenilir masaüstü çözümleri sunuyoruz.",
              "Her projede, işletmenizin ihtiyaçlarını ve müşteri beklentilerini anlayarak, özel masaüstü uygulama çözümleri geliştiriyoruz. Modern teknolojiler kullanarak, hızlı ve güvenli masaüstü uygulamaları oluşturuyoruz.",
              "Masaüstü uygulama geliştirme sürecimiz, platform analizleri ve ihtiyaç belirleme ile başlar. Tek bir uygulamayla tüm işletim sistemlerinde çalışan masaüstü uygulamaları geliştiriyoruz. İnternet bağlantısı olmadan çalışabilme, diğer sistemlerle bağlantılar ve güvenlik konularında da destek sağlayarak, işletmenizin ihtiyaçlarına özel masaüstü çözümler sunuyoruz.",
            ],
          },
        },
        eCommerce: {
          title: "E-Ticaret Çözümleri | Online Mağaza Geliştirme | LAK VERSE",
          description: "Shopify, WooCommerce ve özel e-ticaret çözümleri. Satışlarınızı artıran, kullanıcı dostu ve güvenli online mağazalar kuruyoruz.",
          hero: {
            title: "E-Ticaret Sistemleri",
            subtitle: "Online satış kanallarınızı güçlendirin ve satışlarınızı artırın",
          },
          features: [
            "Shopify ve WooCommerce ile hazır çözümler",
            "İhtiyacınıza özel e-ticaret platformları",
            "Güvenli ödeme sistemleri",
            "Kolay ürün ve stok yönetimi",
            "Google'da üst sıralarda görünme ve pazarlama araçları",
            "Telefon, tablet ve bilgisayarda mükemmel görünüm",
          ],
          content: {
            title: "Profesyonel E-Ticaret Çözümleri",
            paragraphs: [
              "Shopify, WooCommerce ve özel e-ticaret çözümleriyle online satış kanallarınızı güçlendiriyoruz. Satışlarınızı artıran, müşterilerinizin kolayca kullanabileceği ve güvenli online mağazalar kuruyoruz.",
              "Her projede, işletmenizin ürün portföyünü ve satış stratejinizi anlayarak, özel e-ticaret çözümleri geliştiriyoruz. Güvenli ödeme sistemleri, kolay ürün yönetimi ve stok takibi gibi temel özelliklerin yanı sıra, Google'da üst sıralarda görünme ve pazarlama araçlarıyla da satışlarınızı artırmanıza yardımcı oluyoruz.",
              "E-ticaret geliştirme sürecimiz, işletmenizin satış stratejisini ve müşterilerinizi anlamakla başlar. Modern e-ticaret platformları ve özel çözümler kullanarak, kullanıcı dostu ve güvenli online mağazalar oluşturuyoruz. Telefon, tablet ve bilgisayarda mükemmel görünen, hızlı açılan e-ticaret siteleri geliştiriyoruz.",
            ],
          },
        },
        shopify: {
          title: "Shopify Tasarım ve Geliştirme | Shopify Mağaza Kurulumu | LAK VERSE",
          description: "Shopify mağaza tasarımı ve geliştirme hizmetleri. Profesyonel Shopify temaları ve özel çözümlerle online mağazanızı kuruyoruz.",
          hero: {
            title: "Shopify Tasarım - Geliştirme",
            subtitle: "Profesyonel Shopify mağazaları ile online satışlarınızı artırın",
          },
          features: [
            "Markanıza özel Shopify mağaza tasarımı",
            "Shopify uygulamaları ile entegrasyon",
            "Kolay ürün yönetimi ve katalog düzenleme",
            "Güvenli ödeme ve kargo bağlantıları",
            "Google'da üst sıralarda görünme",
            "Telefon, tablet ve bilgisayarda mükemmel görünüm",
          ],
          content: {
            title: "Profesyonel Shopify Tasarım ve Geliştirme",
            paragraphs: [
              "Shopify mağaza tasarımı ve geliştirme hizmetleri sunuyoruz. Profesyonel Shopify temaları ve özel çözümlerle online mağazanızı kuruyoruz.",
              "Her projede, markanızın görsel kimliğini ve ürün portföyünüzü anlayarak, özel Shopify tema tasarımları geliştiriyoruz. Shopify uygulamaları ile entegrasyonlar, kolay ürün yönetimi ve katalog düzenleme gibi temel özelliklerin yanı sıra, güvenli ödeme ve kargo bağlantılarıyla da mağazanızın sorunsuz çalışmasını sağlıyoruz.",
              "Shopify geliştirme sürecimiz, mağazanızın ihtiyaçlarını ve müşterilerinizi anlamakla başlar. Modern Shopify temaları ve özel çözümler kullanarak, kullanıcı dostu ve güvenli online mağazalar oluşturuyoruz. Google'da üst sıralarda görünme ve telefon, tablet ve bilgisayarda mükemmel görünüm ile satışlarınızı artıracak Shopify mağazaları geliştiriyoruz.",
            ],
          },
        },
        woocommerce: {
          title: "WooCommerce Tasarım ve Geliştirme | WordPress E-Ticaret | LAK VERSE",
          description: "WooCommerce tasarım ve geliştirme hizmetleri. WordPress tabanlı e-ticaret çözümleriyle online mağazanızı kuruyoruz.",
          hero: {
            title: "WooCommerce Tasarım - Geliştirme",
            subtitle: "WordPress tabanlı e-ticaret çözümleriyle online satışlarınızı artırın",
          },
          features: [
            "Markanıza özel WooCommerce mağaza tasarımı",
            "İhtiyacınıza özel özellikler ve eklentiler",
            "WordPress ile uyumlu ve özelleştirilebilir yapı",
            "Güvenli ödeme sistemleri entegrasyonu",
            "Kolay stok yönetimi ve sipariş takibi",
            "Google'da üst sıralarda görünme ve güvenlik",
          ],
          content: {
            title: "Profesyonel WooCommerce Tasarım ve Geliştirme",
            paragraphs: [
              "WooCommerce tasarım ve geliştirme hizmetleri sunuyoruz. WordPress tabanlı e-ticaret çözümleriyle online mağazanızı kuruyoruz.",
              "Her projede, markanızın görsel kimliğini ve ürün portföyünüzü anlayarak, özel WooCommerce tema tasarımları geliştiriyoruz. İhtiyacınıza özel özellikler ve eklentiler, kolay ürün yönetimi ve katalog düzenleme gibi temel özelliklerin yanı sıra, güvenli ödeme ve kargo bağlantılarıyla da mağazanızın sorunsuz çalışmasını sağlıyoruz.",
              "WooCommerce geliştirme sürecimiz, mağazanızın ihtiyaçlarını ve müşterilerinizi anlamakla başlar. Modern WooCommerce temaları ve özel çözümler kullanarak, kullanıcı dostu ve güvenli online mağazalar oluşturuyoruz. Google'da üst sıralarda görünme ve güvenlik konularında da uzman desteği sağlayarak, satışlarınızı artıracak WooCommerce mağazaları geliştiriyoruz.",
            ],
          },
        },
        automation: {
          title: "İş Süreci Otomasyonu | Otomasyon Çözümleri | LAK VERSE",
          description: "Tekrarlayan iş süreçlerinizi otomatikleştirin. Sistemler arası entegrasyon, veri senkronizasyonu, otomatik bildirim ve raporlama çözümleriyle zaman ve maliyet tasarrufu sağlayın.",
          hero: {
            title: "Otomasyon",
            subtitle: "Tekrarlayan işleri otomatikleştirin, ekibinizin zamanını değer üreten işlere ayırın",
          },
          features: [
            "İşletmenize özel iş süreci otomasyonları",
            "CRM, ERP, e-ticaret ve diğer sistemler arası entegrasyonlar",
            "Otomatik bildirim ve raporlama sistemleri",
            "Veri işleme, dönüştürme ve senkronizasyon",
            "Zamanlanmış görevler ve olay tetiklemeli iş akışları",
            "Yapay zeka destekli akıllı otomasyonlar",
          ],
          content: {
            title: "Profesyonel Otomasyon Çözümleri",
            paragraphs: [
              "Manuel yürütülen, tekrarlayan iş süreçlerinizi otomatikleştiriyoruz. Formdan gelen talebin CRM'e kaydedilmesinden fatura ve raporların otomatik hazırlanmasına kadar, ekibinizin zamanını alan işleri sizin yerinize çalışan sistemlere dönüştürüyoruz.",
              "Her projede önce mevcut iş akışınızı analiz ediyor, otomasyonun en çok değer katacağı noktaları belirliyoruz. Kullandığınız yazılımlar arasında güvenli bağlantılar kuruyor, verilerin her yerde güncel kalmasını sağlıyor ve süreçlerinizi hatasız ve ölçülebilir hale getiriyoruz.",
              "İhtiyacınıza göre hazır otomasyon platformlarını ya da tamamen size özel geliştirilen çözümleri kullanıyoruz. Zamanlanmış görevler, olay tetiklemeli iş akışları ve yapay zeka destekli adımlarla kurduğumuz otomasyonları devreye aldıktan sonra da izliyor ve sürekli iyileştiriyoruz.",
            ],
          },
        },
        aiServices: {
          title: "AI Servisler | Yapay Zeka Destekli Çözümler | LAK VERSE",
          description: "Yapay zeka teknolojileri ile fotoğraf, video ve metin üretimi. İşletmenizin ihtiyaçlarına özel AI entegrasyonları ve ürünleştirilmiş servisler sunuyoruz.",
          hero: {
            title: "AI Ürünleştirilmiş Servisler",
            subtitle: "Yapay zeka teknolojileri ile iş süreçlerinizi optimize edin",
          },
          features: [
            "Yapay zeka ile içerik üretimi (metin, görsel, video)",
            "Akıllı sohbet robotları ve müşteri hizmetleri",
            "Diğer sistemlerle uyumlu yapay zeka entegrasyonları",
            "Veri analizi ve akıllı raporlama",
            "İşletmenize özel yapay zeka çözümleri",
            "İş süreçlerinizi otomatikleştiren akıllı sistemler",
          ],
          content: {
            title: "Profesyonel AI Servisler",
            paragraphs: [
              "Yapay zeka teknolojileri ile fotoğraf, video ve metin üretimi hizmetleri sunuyoruz. İşletmenizin ihtiyaçlarına özel yapay zeka entegrasyonları ve hazır çözümler geliştiriyoruz.",
              "Her projede, işletmenizin yapay zeka ihtiyaçlarını ve kullanım senaryolarını anlayarak, özel yapay zeka çözümleri geliştiriyoruz. Yapay zeka destekli içerik üretimi, fotoğraf ve video işleme, metin analizi ve üretimi gibi temel özelliklerin yanı sıra, akıllı sohbet robotları ve müşteri destek sistemleriyle de müşteri deneyiminizi geliştiriyoruz.",
              "Yapay zeka servis geliştirme sürecimiz, işletmenizin yapay zeka ihtiyaçlarını ve kullanım senaryolarını anlamakla başlar. Modern yapay zeka teknolojileri kullanarak, özel yapay zeka çözümleri ve hazır servisler geliştiriyoruz. Diğer sistemlerle uyumlu entegrasyonlar ve özel çözümlerle de işletmenizin benzersiz ihtiyaçlarına özel yapay zeka servisler sunuyoruz.",
            ],
          },
        },
        uiUx: {
          title: "UI & UX Tasarım | Kullanıcı Arayüzü ve Deneyimi Tasarımı | LAK VERSE",
          description: "Kullanıcı araştırması, bilgi mimarisi, wireframe, prototip ve arayüz tasarımı. Web ve mobil ürünleriniz için kullanımı kolay, dönüşüm odaklı ve markanıza uygun dijital deneyimler tasarlıyoruz.",
          hero: {
            title: "UI & UX Tasarım",
            subtitle: "Kullanıcılarınızın sevdiği, kolay kullanılan ve iş hedeflerinize hizmet eden dijital deneyimler tasarlıyoruz",
          },
          features: [
            "Kullanıcı araştırması ve persona çalışmaları",
            "Bilgi mimarisi ve kullanıcı akışları",
            "Wireframe ve tıklanabilir prototipler",
            "Web ve mobil için arayüz (UI) tasarımı",
            "Tasarım sistemleri ve bileşen kütüphaneleri",
            "Kullanılabilirlik testleri ve erişilebilirlik",
          ],
          content: {
            title: "Profesyonel UI & UX Tasarım Hizmetleri",
            paragraphs: [
              "İyi bir dijital ürün, güzel görünmenin ötesinde kullanıcısının işini kolaylaştırır. UI & UX tasarım hizmetimizle web siteleri, web yazılımları ve mobil uygulamalar için hem göze hitap eden hem de sezgisel olarak kullanılan arayüzler tasarlıyoruz.",
              "Sürece kullanıcılarınızı ve iş hedeflerinizi anlayarak başlıyoruz. Araştırma ve analizlerin ardından bilgi mimarisini ve kullanıcı akışlarını kurguluyor, wireframe ve tıklanabilir prototiplerle fikirleri geliştirme başlamadan önce test edilebilir hale getiriyoruz.",
              "Onaylanan akışları markanıza uygun, tutarlı ve erişilebilir bir arayüz tasarımına dönüştürüyoruz. Hazırladığımız tasarım sistemleri ve bileşen kütüphaneleri sayesinde geliştirme süreci hızlanıyor, ürününüz büyüdükçe tasarım bütünlüğü korunuyor.",
            ],
          },
        },
      },
    },
    dreamAssistant: {
      title: "Hayalini Anlat",
      description: "Proje hayalinizi anlatın, AI size özel bir vizyon oluştursun",
      placeholder: "Projenizi detaylıca anlatın... Örneğin: 'E-ticaret sitesi istiyorum, ürün kataloğu, sepet, ödeme sistemi ve yönetim paneli olsun...'",
      submit: "Analiz Et",
      analyzing: "AI projenizi analiz ediyor...",
      analyzingSubtext: "Bu birkaç saniye sürebilir",
      storyTitle: "Proje Vizyonunuz",
      servicesTitle: "Önerilen Hizmetler",
      featuresTitle: "Ana Özellikler",
      complexityTitle: "Tahmini Karmaşıklık",
      nextStepsTitle: "Sonraki Adımlar",
      share: "Paylaş",
      shareSuccess: "Link kopyalandı!",
      getQuote: "Teklif Al",
      tryAgain: "Tekrar Dene",
      error: "Bir hata oluştu. Lütfen tekrar deneyin.",
      rateLimitError: "Günlük limit aşıldı. Lütfen yarın tekrar deneyin.",
      complexity: {
        low: "Düşük",
        medium: "Orta",
        high: "Yüksek",
        "very-high": "Çok Yüksek",
      },
    },
    quoteForm: {
      title: "Teklif Al",
      description: "Projeniz için ücretsiz teklif alın",
      personalInfo: "Kişisel Bilgiler",
      projectDetails: "Proje Detayları",
      name: "Ad Soyad",
      company: "Şirket Adı",
      email: "E-posta",
      phone: "Telefon",
      service: "Hizmet Seçiniz",
      selectService: "Hizmet seçin...",
      deadline: "Hedef Teslimat Süresi",
      selectDeadline: "Süre seçin...",
      message: "Proje Detayları",
      send: "Gönder",
      sending: "Gönderiliyor...",
      success: "Teklif talebiniz alındı! En kısa sürede size dönüş yapacağız.",
      error: "Bir hata oluştu. Lütfen tekrar deneyin.",
      deadlineOptions: {
        "0-2": "0-2 Hafta",
        "2-4": "2-4 Hafta",
        "4-8": "4-8 Hafta",
        "8-10": "8-10 Hafta",
        "10+": "10 Hafta ve Yukarısı",
      },
      services: {
        webDesign: "Web Tasarım",
        webDevelopment: "Web Yazılım",
        mobileApp: "Mobil Uygulama",
        desktopApp: "Masaüstü Uygulama",
        eCommerce: "E-Ticaret",
        shopify: "Shopify",
        woocommerce: "WooCommerce",
        automation: "Otomasyon",
        aiServices: "AI Servisler",
        uiUx: "UI & UX Tasarım",
      },
    },
  },
  en: {
    home: {
      title: "Home",
      description: "LAKVERSE - Professional web design, mobile app development, e-commerce solutions and AI-powered digital transformation services. We elevate your brand in the digital world with modern technologies.",
      keywords: "web design, web development, mobile app, e-commerce, artificial intelligence, digital transformation, software agency, Shopify, WooCommerce, Next.js, React",
    },
    nav: {
      services: "Services",
      corporate: "Corporate",
      references: "References",
      blog: "Blog",
      contact: "Contact",
      getQuote: "Get Quote",
    },
    hero: {
      slides: [
        {
          badge: "Web Design",
          highlight: "stand out in",
          subtitle: "the digital world",
          description: "Elevate your brand in the digital world with modern and elegant web designs. We take your business one step ahead with user experience-focused solutions.",
          ctaLink: "/en/services/web-design",
        },
        {
          badge: "Web Development",
          highlight: "professional web",
          subtitle: "solutions",
          description: "We develop powerful, fast, and scalable web applications with Next.js, React, and modern technologies.",
          ctaLink: "/en/services/web-development",
        },
        {
          badge: "Mobile App",
          highlight: "reach your customers",
          subtitle: "with mobile apps",
          description: "Reach your customers anytime, anywhere with iOS and Android applications. Native and cross-platform solutions.",
          ctaLink: "/en/services/mobile-app",
        },
        {
          badge: "E-Commerce",
          highlight: "increase your sales",
          subtitle: "with e-commerce",
          description: "Strengthen your online sales channels with Shopify, WooCommerce, and custom e-commerce solutions.",
          ctaLink: "/en/services/e-commerce",
        },
        {
          badge: "Artificial Intelligence",
          highlight: "shape the future",
          subtitle: "with AI",
          description: "Optimize your business processes and increase your efficiency with AI-powered automation and smart solutions.",
          ctaLink: "/en/services/ai-services",
        },
      ],
      freeQuote: "Get Free Quote",
      viewService: "View Service",
      prevSlide: "Previous slide",
      nextSlide: "Next slide",
    },
    services: {
      badge: "Services",
      title: "Our Services",
      description: "Comprehensive services that accompany you on your digital transformation journey. We take your business one step ahead with modern technologies and AI-powered solutions.",
      items: [
        {
          title: "Web Design",
          description: "Modern, user-focused and SEO-friendly web designs. We design professional websites that reflect your brand's digital identity and increase conversion rates.",
          href: "/en/services/web-design",
          code: "const design = createModernUI();",
        },
        {
          title: "Web Development",
          description: "Scalable web applications with Next.js, React and modern technologies. We develop high-performance, secure and easy-to-maintain software solutions.",
          href: "/en/services/web-development",
          code: "function buildApp() { return <App />; }",
        },
        {
          title: "Mobile App",
          description: "Native and cross-platform mobile applications for iOS and Android. We offer high-performance mobile solutions that prioritize user experience.",
          href: "/en/services/mobile-app",
          code: "const app = new MobileApp();",
        },
        {
          title: "E-Commerce Systems",
          description: "Shopify, WooCommerce and custom e-commerce solutions. We build user-friendly and secure online stores that increase your sales.",
          href: "/en/services/e-commerce",
          code: "shopify.init({ store: 'yours' });",
        },
        {
          title: "Automation",
          description: "Smart solutions that automate your repetitive business processes. We connect your systems and build automations that save time and costs.",
          href: "/en/services/automation",
          code: "workflow.trigger('automation');",
        },
        {
          title: "AI-Powered Services",
          description: "Photo, video and text generation with artificial intelligence technologies. We offer custom AI integrations and productized services tailored to your business needs.",
          href: "/en/services/ai-services",
          code: "ai.generate({ type: 'content' });",
        },
      ],
      moreInfo: "Learn More",
      viewAll: "View All Services",
    },
    cta: {
      title: "Let's Work ",
      titleHighlight: "Together",
      description: "Get a free quote for your project. We are happy to help you on your digital transformation journey. We take your business one step ahead with modern technologies and our professional team.",
      button: "Get Free Quote",
      stats: {
        projects: "Projects",
        satisfaction: "Satisfaction",
        experience: "Years Experience",
      },
    },
    references: {
      badge: "Portfolio",
      title: "Our References",
      description: "Examples from our successful projects. Discover our clients' digital transformation stories and get inspired for your brand.",
      loading: "Loading...",
      empty: "No references found yet.",
      viewCaseStudy: "View Case Study",
      viewAll: "View All References",
      previous: "Previous",
      next: "Next",
      imagePlaceholder: "Image to be added",
    },
    blog: {
      title: "Blog & Content",
      description: "Latest news, trends and expert opinions from the digital world. Current content on web design, e-commerce, artificial intelligence and digital marketing.",
      loading: "Loading...",
      empty: "No blog posts found yet.",
      readMore: "Read More",
      viewAll: "View All Posts",
      noImage: "No Image",
    },
    header: {
      services: "Services",
      corporate: "Corporate",
      references: "References",
      products: "Products",
      blog: "Blog",
      contact: "Contact",
      getQuote: "Get Quote",
      menuOpen: "Open menu",
      menuClose: "Close menu",
      serviceCategories: {
        webDevelopment: "WEB DEVELOPMENT",
        mobileDesktop: "MOBILE & DESKTOP",
        eCommerce: "E-COMMERCE",
        automationAI: "AUTOMATION & AI",
      },
      serviceItems: {
        webDesign: { label: "Web Design", description: "Modern and elegant web designs" },
        webDevelopment: { label: "Web Development", description: "Custom web software for your business" },
        mobileApp: { label: "Mobile App", description: "iPhone and Android applications" },
        desktopApp: { label: "Desktop App", description: "Windows, Mac and Linux applications" },
        eCommerce: { label: "E-Commerce", description: "Custom e-commerce solutions" },
        shopify: { label: "Shopify", description: "Shopify store design" },
        woocommerce: { label: "WooCommerce", description: "WooCommerce integrations" },
        automation: { label: "Automation", description: "Automate your business processes" },
        aiServices: { label: "AI Services", description: "AI-powered solutions" },
        uiUx: { label: "UI & UX Design", description: "User-centered interface and experience design" },
      },
      corporateItems: {
        about: "About Us",
        corporateIdentity: "Corporate Identity",
        laki: "Meet Laki",
        career: "Career",
      },
    },
    footer: {
      sections: {
        webDevelopment: "Web Development",
        mobileDesktop: "Mobile & Desktop",
        eCommerce: "E-Commerce",
        automationAI: "Automation & AI",
        corporate: "Corporate",
        resources: "Resources",
      },
      newsletter: {
        title: "Stay Updated",
        description: "Subscribe to stay informed about our new projects, technology news and special offers.",
      },
      copyright: "©",
      allRightsReserved: "All rights reserved.",
      privacyPolicy: "Privacy Policy",
      kvkk: "Data Protection Law",
      termsOfUse: "Terms of Use",
      cookiePolicy: "Cookie Policy",
    },
    dreamAssistant: {
      title: "Tell Your Dream",
      description: "Describe your project dream, AI will create a personalized vision",
      placeholder: "Describe your project in detail... For example: 'I want an e-commerce site with product catalog, cart, payment system and admin panel...'",
      submit: "Analyze",
      analyzing: "AI is analyzing your project...",
      analyzingSubtext: "This may take a few seconds",
      storyTitle: "Your Project Vision",
      servicesTitle: "Suggested Services",
      featuresTitle: "Key Features",
      complexityTitle: "Estimated Complexity",
      nextStepsTitle: "Next Steps",
      share: "Share",
      shareSuccess: "Link copied!",
      getQuote: "Get Quote",
      tryAgain: "Try Again",
      error: "An error occurred. Please try again.",
      rateLimitError: "Daily limit exceeded. Please try again tomorrow.",
      complexity: {
        low: "Low",
        medium: "Medium",
        high: "High",
        "very-high": "Very High",
      },
    },
    quoteForm: {
      title: "Get Quote",
      description: "Get a free quote for your project",
      personalInfo: "Personal Information",
      projectDetails: "Project Details",
      name: "Full Name",
      company: "Company Name",
      email: "Email",
      phone: "Phone",
      service: "Select Services",
      selectService: "Select services...",
      deadline: "Target Delivery Time",
      selectDeadline: "Select time...",
      message: "Project Details",
      send: "Send",
      sending: "Sending...",
      success: "Your quote request has been received! We will get back to you as soon as possible.",
      error: "An error occurred. Please try again.",
      deadlineOptions: {
        "0-2": "0-2 Weeks",
        "2-4": "2-4 Weeks",
        "4-8": "4-8 Weeks",
        "8-10": "8-10 Weeks",
        "10+": "10+ Weeks",
      },
      services: {
        webDesign: "Web Design",
        webDevelopment: "Web Development",
        mobileApp: "Mobile App",
        desktopApp: "Desktop App",
        eCommerce: "E-Commerce",
        shopify: "Shopify",
        woocommerce: "WooCommerce",
        automation: "Automation",
        aiServices: "AI Services",
        uiUx: "UI & UX Design",
      },
    },
    newsletter: {
      placeholder: "Your email address",
      button: "Subscribe",
      sending: "Sending...",
      sent: "Sent!",
      success: "Successfully subscribed! Thank you.",
      error: "An error occurred. Please try again.",
      invalidEmail: "Please enter a valid email address.",
    },
    admin: {
      dashboard: {
        title: "Admin Panel",
        description: "Manage your projects and customers",
        stats: {
          totalProjects: "Total Projects",
          activeProjects: "Active Projects",
          completedProjects: "Completed Projects",
          totalCustomers: "Total Customers",
        },
      },
      projects: {
        title: "Projects",
        newProject: "New Project",
        editProject: "Edit Project",
        noProjects: "No projects found yet.",
        loading: "Loading...",
        fields: {
          title: "Project Title",
          client: "Client",
          description: "Description",
          deadline: "Deadline",
          status: "Status",
          progress: "Progress (%)",
          locale: "Language",
          customer: "Select Customer",
        },
        status: {
          inProgress: "In Progress",
          review: "Under Review",
          completed: "Completed",
          onHold: "On Hold",
        },
        actions: {
          save: "Save",
          cancel: "Cancel",
          delete: "Delete",
          edit: "Edit",
          view: "View",
        },
        messages: {
          createSuccess: "Project created successfully.",
          updateSuccess: "Project updated successfully.",
          deleteSuccess: "Project deleted successfully.",
          createError: "An error occurred while creating the project.",
          updateError: "An error occurred while updating the project.",
          deleteError: "An error occurred while deleting the project.",
        },
      },
      references: {
        title: "References",
        newReference: "New Reference",
        editReference: "Edit Reference",
      },
      blog: {
        title: "Blog",
        newPost: "New Post",
        editPost: "Edit Post",
      },
    },
    cookieConsent: {
      title: "We Use Cookies",
      description: "We use cookies on our website to provide you with the best experience and to analyze your site usage. For detailed information about cookies, please visit our",
      cookiePolicyLink: "Cookie Policy",
      reject: "Reject",
      accept: "Accept",
    },
    scrollToTop: {
      label: "Top",
      ariaLabel: "Scroll to top",
    },
    pages: {
      about: {
        title: "About Us | LAKVERSE - Modern Software Solutions",
        description: "Information about LAKVERSE. A software agency specialized in web design, mobile app development, e-commerce solutions and AI-powered automation systems.",
        hero: {
          title: "About Us",
          subtitle: "We take your business to the digital world with modern software solutions",
        },
        sections: {
          intro: {
            title: "Your Trusted Partner in Digital Transformation",
            paragraphs: [
              "As LAKVERSE, we are a software agency specialized in web design, mobile app development, e-commerce solutions and AI-powered automation systems. We accompany our clients on their digital transformation journey by offering them the best technological solutions.",
              "We develop scalable and performant applications using modern technologies such as Next.js, React, Supabase. Our team consists of experienced professionals in software development, design, project management and customer relations.",
              "In every project, we conduct detailed analyses to understand our clients' needs and offer the most suitable solutions. Customer satisfaction and project quality are our top priorities.",
              "Our web design process begins with understanding your brand's target audience. We develop a custom design strategy for your brand by examining user research, competitor analysis and industry trends.",
            ],
          },
          mission: {
            title: "Our Mission",
            badge: "Core Value",
            description: "As LAKVERSE, our primary goal is to offer our clients the highest quality software solutions. To be by their side on their digital transformation journey and offer the best technological solutions.",
            principles: [
              "Prioritizing customer satisfaction",
              "Working with a continuous improvement mindset",
              "Using modern technologies and best practices",
              "Working with experienced and trained staff",
              "Executing project quality meticulously",
            ],
          },
          vision: {
            title: "Our Vision",
            badge: "Future Goal",
            description: "To become Turkey's leading software agency and be a pioneer in the industry with AI technologies. To become a brand referenced in digital transformation.",
            principles: [
              "Being in a leading position in the industry",
              "Using innovative technologies as a pioneer",
              "Focusing on customer success",
              "Continuous development and learning culture",
              "Being a reference in digital transformation",
            ],
          },
          values: {
            title: "Our Values",
            badge: "Corporate Culture",
            description: "Our working principles focused on customer orientation, innovation, transparency and quality. We aim for the highest standards in every project.",
            principles: [
              "Customer-oriented approach",
              "Innovation and creativity",
              "Transparency and honesty",
              "Quality and excellence",
              "Teamwork and cooperation",
            ],
          },
          experience: {
            title: "Experience",
            badge: "Industry Leader",
            description: "We are a trusted partner in the industry with years of experience and successful projects. We are pioneers in the industry with our culture of continuous learning and development.",
            principles: [
              "Years of experience and expertise",
              "Successful project portfolio",
              "Continuous learning and development",
              "Trusted partner in the industry",
              "Innovative solutions and approaches",
            ],
          },
          principlesTitle: "CORE PRINCIPLES",
          importance: {
            title: "The Importance of Our Values",
            description: "As LAKVERSE, we conduct all our values transparently and responsibly. These values form the basis of our way of doing business and enable us to create sustainable value for our clients, employees and society.",
          },
        },
      },
      corporateIdentity: {
        title: "Corporate Identity | LAKVERSE - Brand Identity and Design",
        description: "LAKVERSE corporate identity, logo usage, color palette, typography and brand values. Our corporate identity standards as a professional software agency.",
        hero: {
          title: "Corporate Identity",
          subtitle: "Our brand's visual identity and corporate standards. As LAKVERSE, we offer a consistent and professional brand experience.",
        },
        logo: {
          title: "Logo Usage",
          subtitle: "Our logo is the most important visual element of our brand. Its correct use is critical for our brand integrity.",
          dark: {
            title: "Dark Background",
            description: "White logo is used on dark backgrounds. The minimum spacing area should be half the logo height.",
          },
          light: {
            title: "Light Background",
            description: "Colored logo is used on light backgrounds. Sufficient space should be left around the logo.",
          },
        },
        colors: {
          title: "Color Palette",
          subtitle: "Our brand's color palette is designed to create a consistent and professional visual identity.",
          primary: {
            title: "Primary Color",
            description: "The primary brand color forms the basis of our corporate identity. It is used in logos, headings and accent elements.",
          },
          accent: {
            title: "Accent Color",
            description: "The accent color is used in secondary elements, hover effects and gradient backgrounds.",
          },
        },
        typography: {
          title: "Typography",
          subtitle: "Our brand's typographic identity combines readability and modern appearance.",
          mainFont: "Main Font: JetBrains Mono",
          examples: {
            light: "Light accents",
            regular: "Default text",
            bold: "Headings",
          },
        },
        brandValues: {
          title: "Brand Values",
          subtitle: "As LAKVERSE, these values form the basis of our brand and guide us in all our work.",
          items: [
            {
              title: "Reliability",
              description: "Strong relationships we build with our business partners and our principle of keeping our word.",
            },
            {
              title: "Innovation",
              description: "Being a pioneer in the industry with continuous development and innovative solutions.",
            },
            {
              title: "Quality",
              description: "Our quality-focused approach that aims for the highest standards in every project.",
            },
            {
              title: "Transparency",
              description: "Ensuring customer satisfaction through open communication and transparent processes.",
            },
          ],
        },
      },
      career: {
        hero: {
          title: "Career",
          subtitle: "Join us on the digital transformation journey. Work with modern technologies, learn continuously and take your career to the next level.",
        },
        whyJoin: {
          title: "Why LAK VERSE?",
          subtitle: "Modern technologies, creative projects and a supportive team environment. Shape your career with us.",
          items: [
            {
              title: "Career Development",
              description: "Continuous learning and development opportunities",
            },
            {
              title: "Health & Wellness",
              description: "Comprehensive health insurance and wellness package",
            },
            {
              title: "Flexible Work",
              description: "Remote and hybrid work options",
            },
            {
              title: "Team Culture",
              description: "Supportive and collaborative team environment",
            },
          ],
        },
        openings: {
          title: "Open Positions",
          noOpenings: "We currently have no active job postings.",
          generalApplication: {
            title: "General Application",
            description: "Fill out the general application form so we can contact you when a position suitable for your experience and interests opens up.",
            button: "Submit General Application",
          },
        },
      },
      careerApplication: {
        hero: {
          title: "Career Application",
          subtitle: "Fill out the application form to join the LAK VERSE family. Share your skills and experience.",
        },
        sections: {
          personalInfo: "Personal Information",
          professionalInfo: "Professional Information",
          resume: "Resume",
          coverLetter: "Cover Letter",
        },
        fields: {
          name: "Full Name",
          email: "Email Address",
          phone: "Phone Number",
          position: "Position Applied For",
          selectPosition: "Select Position",
          experience: "Experience Duration",
          selectExperience: "Select Experience Duration",
          linkedin: "LinkedIn Profile",
          portfolio: "Portfolio / GitHub",
          resumeUpload: "Upload CV / Resume",
          resumePlaceholder: "Upload file in PDF, DOC or DOCX format",
          resumeMaxSize: "Maximum 5MB",
          coverLetterLabel: "Why LAK VERSE?",
          coverLetterPlaceholder: "Tell us about yourself, explain why you are suitable for this position and why you want to work at LAK VERSE...",
        },
        experienceOptions: {
          "0-1": "0-1 Years",
          "1-3": "1-3 Years",
          "3-5": "3-5 Years",
          "5-7": "5-7 Years",
          "7+": "7+ Years",
        },
        positionOptions: {
          seniorFrontend: "Senior Frontend Developer",
          uiUx: "UI/UX Designer",
          mobileApp: "Mobile App Developer",
          aiMl: "AI/ML Engineer",
          backend: "Backend Developer",
          fullstack: "Full Stack Developer",
          general: "General Application",
        },
        buttons: {
          back: "← Back to Career page",
          submit: "Submit Application",
          submitting: "Submitting...",
        },
        messages: {
          resumeRequired: "Please upload your CV/resume file.",
          submitError: "Your application could not be submitted. Please try again.",
          submitSuccess: "Your application has been received! We will get back to you as soon as possible.",
        },
      },
      products: {
        title: "Our Products | LAKVERSE - Products in Development",
        description: "Applications developed by LAKVERSE, delivered to users through subscription systems. Cost tracking, AI-powered patient tracking and AI image generation applications.",
        hero: {
          title: "Our Products",
          subtitle: "Applications developed by LAKVERSE, delivered to users through subscription systems",
        },
        intro: {
          title: "Coming Soon",
          description: "As LAKVERSE, we develop custom solutions needed in different sectors. These products are independent web applications delivered to users through subscription systems.",
        },
        comingSoon: "Coming Soon",
        subscriptionBased: "Subscription System",
        items: [
          {
            title: "Cost Tracking",
            description: "A comprehensive cost management system that enables businesses to track and analyze their costs. Ensure financial control of your business with project-based cost tracking, budget management and detailed reporting features.",
            features: [
              "Project-based cost tracking",
              "Budget management and planning",
              "Detailed cost reports",
              "Expense categories and analysis",
              "Income-expense comparisons",
            ],
          },
          {
            title: "AI-Powered Patient Tracking",
            description: "An AI-powered patient tracking and management system developed for the healthcare sector. Optimize the operations of healthcare institutions with secure storage of patient information, appointment management and AI-powered preliminary diagnosis suggestions.",
            features: [
              "Secure patient information management",
              "Appointment tracking and management",
              "AI-powered preliminary diagnosis suggestions",
              "Patient history and records",
              "Doctor-patient communication platform",
            ],
          },
          {
            title: "AI Image Generation",
            description: "A creative content creation platform that produces high-quality visuals using artificial intelligence technologies. Create professional images from text-based prompts, edit existing images and produce AI-powered visual content for your creative projects.",
            features: [
              "Text-based image generation",
              "High-quality AI images",
              "Image editing and enhancement",
              "Multiple style and theme options",
              "Batch image generation",
            ],
          },
        ],
      },
      quoteRequest: {
        hero: {
          title: "Get Quote",
          subtitle: "Fill out the form to get a detailed quote for your project. Our expert team will offer you custom solutions as soon as possible.",
          benefits: [
            { text: "Response Within 24 Hours" },
            { text: "Free Quote" },
            { text: "Fast Solution" },
            { text: "Expert Support" },
          ],
        },
        sections: {
          personalInfo: "Personal Information",
          projectDetails: "Project Details",
          projectAbout: "About Project",
        },
        fields: {
          name: "Full Name",
          company: "Company Name",
          email: "Email Address",
          phone: "Phone Number",
          service: "Select Service(s)",
          selectService: "Select services...",
          deadline: "Target Delivery Time",
          selectDeadline: "Select delivery time",
          message: "Project Details",
          messagePlaceholder: "Please provide detailed information about your project. You can share your goals, expectations and special requirements...",
        },
        buttons: {
          submit: "Request Quote",
          submitting: "Sending...",
        },
        messages: {
          error: "An error occurred. Please try again.",
          success: "Your quote request has been received! We will get back to you as soon as possible.",
        },
      },
      contact: {
        hero: {
          title: "Contact",
          subtitle: "Get in touch with us for your projects",
        },
        form: {
          title: "Contact Us",
          subtitle: "You can reach us by filling out the form or use our contact information below.",
          fields: {
            name: "Full Name",
            email: "Email",
            phone: "Phone",
            subject: "Subject",
            message: "Message",
          },
          button: "Send",
          submitting: "Sending...",
        },
        info: {
          contactInfo: "Contact Information",
          workingHours: "Working Hours",
          followUs: "Follow Us",
          hours: {
            weekdays: "Monday - Friday: 09:00 - 18:00",
            saturday: "Saturday: 10:00 - 16:00",
            sunday: "Sunday: Closed",
          },
        },
        messages: {
          error: "Message could not be sent. Please try again.",
          success: "Your message has been sent! We will get back to you as soon as possible.",
        },
      },
      privacyPolicy: {
        title: "Privacy Policy",
        description: "LAKVERSE privacy policy and information about personal data protection.",
        lastUpdated: "Last updated",
        sections: {
          general: {
            title: "1. General Information",
            paragraphs: [
              "As LAKVERSE, we attach great importance to the protection of your personal data. This Privacy Policy explains how your personal data collected when you visit our website or use our services is collected, used, protected and shared.",
              "This policy has been prepared in accordance with the Personal Data Protection Law No. 6698 (\"KVKK\") and related legislation.",
            ],
          },
          dataController: {
            title: "2. Data Controller",
            paragraphs: ["The data controller responsible for processing your personal data:"],
            company: "LAKVERSE",
            email: "Email",
            web: "Web",
          },
          collectedData: {
            title: "3. Collected Personal Data",
            intro: "The following personal data may be collected within the scope of our services:",
            identity: {
              title: "3.1. Identity Information",
              items: ["Name, surname", "Email address", "Phone number", "Job title"],
            },
            contact: {
              title: "3.2. Contact Information",
              items: ["Email address", "Phone number", "Address information"],
            },
            transaction: {
              title: "3.3. Transaction Information",
              items: ["Project requests and details", "Quote form information", "Contact form information"],
            },
            technical: {
              title: "3.4. Technical Information",
              items: ["IP address", "Browser information", "Cookie information", "Visited pages", "Visit date and time"],
            },
          },
          processingPurposes: {
            title: "4. Purposes of Processing Personal Data",
            intro: "Your collected personal data is processed for the following purposes:",
            items: [
              "Evaluation and fulfillment of your service requests",
              "Preparing and presenting quotes",
              "Project management and tracking",
              "Customer relationship management",
              "Communication and information activities",
              "Fulfillment of legal obligations",
              "Website analysis and improvement",
              "Security and fraud prevention",
              "Marketing and promotional activities (with explicit consent)",
            ],
          },
          legalBasis: {
            title: "5. Legal Basis for Processing Personal Data",
            intro: "Your personal data is processed for the following legal reasons:",
            items: [
              "Your explicit consent",
              "Being directly related to the establishment or performance of the contract",
              "Fulfillment of our legal obligation",
              "Fulfillment of our legal obligations",
              "Our legitimate interests (improving service quality, ensuring security)",
            ],
          },
          dataSharing: {
            title: "6. Sharing of Personal Data",
            paragraphs: [
              "Your personal data may be shared with the following third parties for the purposes stated above:",
            ],
            items: [
              "Public institutions and organizations due to our legal obligations",
              "Our service providers (hosting, email services, analytics tools)",
              "Our business partners and suppliers",
              "Our legal advisors and accounting firms",
            ],
          },
          cookies: {
            title: "7. Cookies",
            paragraphs: [
              "Cookies are used on our website to improve user experience and analyze site performance. For detailed information about cookie usage, you can visit our KVKK Disclosure Text page.",
            ],
          },
          retention: {
            title: "8. Retention Period of Personal Data",
            paragraphs: [
              "Your personal data is stored for the period required by the processing purpose and within the legal retention periods. When this period expires, your personal data is deleted, destroyed or anonymized in accordance with the law.",
            ],
          },
          security: {
            title: "9. Security of Your Personal Data",
            intro: "We take technical and administrative security measures to ensure the security of your personal data:",
            items: [
              "SSL/TLS encryption technologies",
              "Secure server infrastructure",
              "Regular security updates",
              "Access control and authorization",
              "Regular backup and data recovery procedures",
            ],
          },
          rights: {
            title: "10. Your Rights",
            intro: "You have the following rights under KVKK:",
            items: [
              "Learning whether your personal data is processed",
              "Requesting information if processed",
              "Learning the purpose of processing and whether they are used in accordance with their purpose",
              "Knowing the third parties to whom personal data is transferred domestically or abroad",
              "Requesting correction if incomplete or incorrectly processed",
              "Requesting deletion or destruction within the framework of the conditions stipulated in KVKK",
              "Requesting that correction, deletion, destruction operations be notified to third parties to whom personal data is transferred",
              "Objecting to the emergence of a result against the person by analyzing the processed data exclusively through automated systems",
              "Requesting compensation for damage in case of damage due to unlawful processing of personal data",
            ],
          },
          application: {
            title: "11. Application Method",
            paragraphs: [
              "To exercise your rights stated above, you can send an email to info@lakverse.com with documents identifying your identity or apply in writing.",
              "Your application will be finalized within 30 (thirty) days at the latest, depending on the nature of your request.",
            ],
          },
          changes: {
            title: "12. Changes",
            paragraphs: [
              "This Privacy Policy may be changed due to changes in legal regulations or updates in our business processes. Important changes will be announced on our website.",
            ],
          },
          contact: {
            title: "13. Contact",
            paragraphs: ["You can contact us for questions about our privacy policy:"],
            company: "LAKVERSE",
            email: "Email",
            web: "Web",
          },
        },
      },
      cookiePolicy: {
        title: "Cookie Policy",
        description: "LAKVERSE cookie usage policy and information about cookie preferences.",
        lastUpdated: "Last updated",
        sections: {
          whatIs: {
            title: "1. What is a Cookie?",
            paragraphs: [
              "Cookies are small text files saved to your device (computer, tablet, phone) when you visit websites. Cookies ensure the website functions properly, improve user experience and help us analyze site performance.",
            ],
          },
          purpose: {
            title: "2. Purpose of Cookie Usage",
            intro: "Cookies are used on our website for the following purposes:",
            items: [
              "Ensuring basic website functions work",
              "Remembering user preferences",
              "Analyzing and improving site performance",
              "Personalizing user experience",
              "Ensuring security",
              "Marketing and advertising activities (with explicit consent)",
            ],
          },
          types: {
            title: "3. Types of Cookies We Use",
            required: {
              title: "3.1. Required Cookies",
              paragraphs: [
                "These cookies are required for the basic functions of the website to work. The website will not function properly without these cookies. User consent is not obtained for these cookies because they are necessary for the website to function.",
              ],
              examples: {
                title: "Examples:",
                items: ["Session management cookies", "Security cookies", "Language preference cookies"],
              },
            },
            analytical: {
              title: "3.2. Analytical Cookies",
              paragraphs: [
                "These cookies help us understand how the website is used. They collect information such as visitor count, most visited pages, user behavior. This information is anonymous and does not contain your personal identification information.",
              ],
              tools: {
                title: "Analytical Tools We Use:",
                items: ["Google Analytics (with anonymous IP address)", "Website performance analysis tools"],
              },
            },
            functional: {
              title: "3.3. Functional Cookies",
              paragraphs: [
                "These cookies enable advanced features and personalization of the website. They remember your preferences and provide you with a better experience.",
              ],
              examples: {
                title: "Examples:",
                items: ["Language preference", "Regional settings", "User preferences"],
              },
            },
            marketing: {
              title: "3.4. Marketing Cookies",
              paragraphs: [
                "These cookies are used to show you more relevant ads and content. These cookies are only used with your explicit consent.",
              ],
              examples: {
                title: "Examples:",
                items: ["Ad personalization", "Content recommendations", "Social media integrations"],
              },
            },
          },
          thirdParty: {
            title: "4. Third-Party Cookies",
            paragraphs: [
              "Third-party cookies placed by our service providers are also used on our website. These cookies are used for analytics, marketing and other services.",
            ],
            services: {
              title: "Third-Party Services:",
              items: ["Google Analytics", "Social media platforms (LinkedIn, etc.)", "Hosting and infrastructure providers"],
            },
          },
          retention: {
            title: "5. Cookie Retention Period",
            intro: "Cookies are stored for different periods depending on their usage purposes:",
            items: [
              { type: "Session Cookies", duration: "Deleted when browser is closed" },
              { type: "Persistent Cookies", duration: "Remain on your device for a certain period (usually 1-2 years)" },
              { type: "Analytical Cookies", duration: "Usually stored for 2 years" },
              { type: "Marketing Cookies", duration: "Until your explicit consent is withdrawn or for a certain period (usually 1 year)" },
            ],
          },
          management: {
            title: "6. Managing Your Cookie Preferences",
            paragraphs: [
              "You can manage your cookie preferences from your browser settings. However, disabling some cookies may cause some website features to not work properly.",
            ],
            browserSettings: {
              title: "6.1. Browser Settings",
              intro: "To manage cookie settings in popular browsers:",
              browsers: [
                { name: "Google Chrome", instructions: "Settings → Privacy and security → Cookies and other site data" },
                { name: "Mozilla Firefox", instructions: "Options → Privacy & Security → Cookies and site data" },
                { name: "Safari", instructions: "Preferences → Privacy → Cookies and website data" },
                { name: "Microsoft Edge", instructions: "Settings → Privacy, search, and services → Cookies and site permissions" },
              ],
            },
            tools: {
              title: "6.2. Cookie Management Tools",
              paragraphs: [
                "A cookie management panel is provided on our website to manage your cookie preferences. You can change your cookie preferences on your first visit or from your browser settings.",
              ],
            },
          },
          disabling: {
            title: "7. Effects of Disabling Cookies",
            intro: "If you disable cookies, some website features may not work properly:",
            items: [
              "Session management issues",
              "Your preferences not being saved",
              "Personalized content not being displayed",
              "Form data being lost",
              "Decreased site performance",
            ],
          },
          mobile: {
            title: "8. Cookie Management on Mobile Devices",
            intro: "You can also manage your cookie preferences on mobile devices:",
            items: [
              { platform: "iOS (Safari)", instructions: "Settings → Safari → Privacy & Security → Block Cookies" },
              { platform: "Android (Chrome)", instructions: "Chrome menu → Settings → Site settings → Cookies" },
            ],
          },
          changes: {
            title: "9. Cookie Policy Changes",
            paragraphs: [
              "This Cookie Policy may be changed due to changes in legal regulations or website updates. Important changes will be announced on our website.",
            ],
          },
          contact: {
            title: "10. Contact",
            paragraphs: ["You can contact us for questions about our cookie policy:"],
            company: "LAKVERSE",
            email: "Email",
            web: "Web",
          },
        },
      },
      termsOfUse: {
        title: "Terms of Use",
        description: "LAKVERSE terms of use and service conditions information.",
        lastUpdated: "Last updated",
        sections: {
          general: {
            title: "1. General Provisions",
            paragraphs: [
              "These Terms of Use set forth the rules for the use of the LAKVERSE website and services. By visiting our website or using our services, you are deemed to have accepted these terms.",
              "As LAKVERSE, we reserve the right to change these terms without prior notice. Changes will be effective from the date they are published on our website.",
            ],
          },
          services: {
            title: "2. Description of Services",
            intro: "LAKVERSE offers the following services:",
            items: [
              "Web design and development services",
              "Mobile app development",
              "E-commerce solutions",
              "Software development and consulting",
              "AI-powered automation systems",
              "Digital transformation consulting",
            ],
          },
          obligations: {
            title: "3. User Obligations",
            intro: "You must comply with the following rules when using our website and services:",
            items: [
              "Acting in accordance with laws and regulations",
              "Not violating others' rights",
              "Not violating copyright, trademark or other intellectual property rights",
              "Not uploading malicious software, viruses or malicious code",
              "Not misusing our services or threatening system security",
              "Not sharing false, misleading or illegal information",
            ],
          },
          intellectualProperty: {
            title: "4. Intellectual Property Rights",
            paragraphs: [
              "All content on our website (texts, images, logos, designs, software, etc.) belongs to LAKVERSE and is protected by copyright, trademark and other intellectual property laws.",
              "Unauthorized copying, reproduction, distribution or commercial use of our content is prohibited. However, you may share our content for personal and non-commercial purposes only.",
            ],
          },
          contracts: {
            title: "5. Service Contracts",
            paragraphs: [
              "Service contracts to be made with LAKVERSE are regulated by a separate contract text. These terms of use are general provisions, and the conditions specified in special contracts take precedence.",
              "Service fees, payment terms, delivery times and other details are determined separately for each project and communicated to the customer in writing.",
            ],
          },
          warranty: {
            title: "6. Warranty and Limitation of Liability",
            paragraphs: [
              "LAKVERSE strives to provide its services in the best way within the framework of current technology and knowledge. However, it does not guarantee that our services will be uninterrupted, error-free or secure.",
              "LAKVERSE cannot be held liable for direct, indirect, special or consequential damages arising from the use of the website or services.",
            ],
          },
          thirdParty: {
            title: "7. Third-Party Links",
            paragraphs: [
              "Links to third-party websites may be found on our website. These links are for informational purposes only and LAKVERSE is not responsible for the content or privacy practices of these sites.",
            ],
          },
          dataSecurity: {
            title: "8. Data Security",
            paragraphs: [
              "LAKVERSE takes technical and administrative measures to ensure the security of your personal data. However, we cannot guarantee the complete security of data transmission over the internet.",
              "For detailed information about the processing of your personal data, you can review our Privacy Policy.",
            ],
          },
          cancellation: {
            title: "9. Cancellation and Refund",
            paragraphs: [
              "Cancellation and refund conditions of service contracts are determined separately for each project and are included in the contract text. Generally, refunds are not made for projects that have started.",
            ],
          },
          changes: {
            title: "10. Changes and Updates",
            paragraphs: [
              "LAKVERSE reserves the right to change these terms of use at any time. Important changes will be announced on our website.",
              "We recommend that you visit this page regularly to stay informed about changes.",
            ],
          },
          law: {
            title: "11. Applicable Law and Jurisdiction",
            paragraphs: [
              "These terms of use are subject to the laws of the Republic of Turkey. Disputes arising from these terms will be resolved in the courts of the Republic of Turkey.",
            ],
          },
          contact: {
            title: "12. Contact",
            paragraphs: ["You can contact us for questions about the terms of use:"],
            company: "LAKVERSE",
            email: "Email",
            phone: "Phone",
            web: "Web",
          },
        },
      },
      kvkk: {
        title: "Data Protection Law Disclosure Text",
        description: "LAKVERSE Personal Data Protection Law (KVKK) disclosure text.",
        subtitle: "Prepared in accordance with Law No. 6698 on the Protection of Personal Data.",
        lastUpdated: "Last updated",
        sections: {
          dataController: {
            title: "1. Data Controller",
            paragraphs: [
              "In accordance with Law No. 6698 on the Protection of Personal Data (\"KVKK\"), your personal data will be processed within the framework specified below.",
            ],
            company: "LAKVERSE",
            email: "Email",
            web: "Web",
          },
          processedData: {
            title: "2. Processed Personal Data",
            intro: "Within the scope of our services, your personal data in the following categories is processed:",
            identity: {
              title: "2.1. Identity Data",
              items: ["Name, surname", "Turkish ID number (as required by legal obligations)", "Date of birth"],
            },
            contact: {
              title: "2.2. Contact Data",
              items: ["Email address", "Phone number", "Address information", "Fax number"],
            },
            customer: {
              title: "2.3. Customer Transaction Data",
              items: ["Project requests", "Quote information", "Contract information", "Invoice information", "Payment information"],
            },
            marketing: {
              title: "2.4. Marketing Data",
              items: ["Marketing and communication preferences", "Campaign information", "Subscription information"],
            },
            security: {
              title: "2.5. Transaction Security Data",
              items: ["IP address", "Cookie information", "Log records", "Browser information"],
            },
            media: {
              title: "2.6. Visual and Audio Records",
              items: ["Photos (project images, event photos)", "Video recordings (project presentations)"],
            },
          },
          processingPurposes: {
            title: "3. Purposes of Processing Personal Data",
            intro: "Your personal data is processed for the following purposes:",
            items: [
              "Evaluation and fulfillment of your service requests",
              "Preparing, presenting and executing quote and contract processes",
              "Project management, tracking and delivery",
              "Customer relationship management and increasing customer satisfaction",
              "Conducting communication activities",
              "Executing invoice, invoicing and accounting operations",
              "Fulfillment of legal obligations",
              "Resolution of legal disputes",
              "Website analysis and improvement of user experience",
              "Security and fraud prevention activities",
              "Marketing and promotional activities (with explicit consent)",
              "Newsletter and campaign sending (with explicit consent)",
            ],
          },
          legalBasis: {
            title: "4. Legal Basis for Processing Personal Data",
            intro: "Your personal data is processed for the following legal reasons:",
            items: [
              { law: "KVKK Art. 5/2-a", description: "Existence of your explicit consent" },
              { law: "KVKK Art. 5/2-c", description: "Being directly related to the establishment or performance of the contract" },
              { law: "KVKK Art. 5/2-e", description: "Being necessary for the data controller to fulfill its legal obligation" },
              { law: "KVKK Art. 5/2-f", description: "The processed data being made public by the data subject" },
              { law: "KVKK Art. 5/2-ç", description: "Data processing being necessary for the establishment, exercise or protection of a right" },
              { law: "KVKK Art. 5/2-f", description: "Data processing being necessary for our legitimate interests" },
            ],
          },
          dataTransfer: {
            title: "5. Transfer of Personal Data",
            paragraphs: [
              "Your personal data may be transferred to the following third parties for the purposes stated above:",
            ],
            domestic: {
              title: "5.1. Domestic Transfers",
              items: [
                "Public institutions and organizations due to our legal obligations",
                "Our service providers (hosting, cloud storage, email services)",
                "Analytics and marketing tool providers",
                "Payment processing companies",
                "Our legal advisors and accounting firms",
                "Our business partners and suppliers",
              ],
            },
            international: {
              title: "5.2. International Transfers",
              paragraphs: [
                "Your personal data may be transferred to foreign countries where our service providers are located. These transfers are made to countries with adequate protection or with written undertakings in countries without adequate protection, within the framework of KVKK Art. 9 provisions and Personal Data Protection Board decisions.",
              ],
            },
          },
          collection: {
            title: "6. Method of Collecting Personal Data",
            intro: "Your personal data is collected by the following methods:",
            items: [
              "Contact forms on our website",
              "Quote request forms",
              "Email communication",
              "Phone calls",
              "Face-to-face meetings",
              "Social media platforms",
              "Cookies and similar technologies",
              "Technical data collected automatically (log files, IP address, etc.)",
            ],
          },
          cookies: {
            title: "7. Cookies",
            paragraphs: [
              "Cookies are used on our website to improve user experience, analyze site performance and provide personalized content.",
            ],
            types: {
              title: "7.1. Types of Cookies Used",
              items: [
                { type: "Required Cookies", description: "Cookies required for basic website functions to work" },
                { type: "Analytical Cookies", description: "Cookies used to analyze site usage" },
                { type: "Functional Cookies", description: "Cookies used to remember user preferences" },
                { type: "Marketing Cookies", description: "Cookies used for personalized ads (with explicit consent)" },
              ],
            },
            management: {
              title: "Managing Your Cookie Preferences",
              paragraphs: [
                "You can manage your cookie preferences from your browser settings. However, disabling some cookies may cause some website features to not work properly.",
              ],
            },
          },
          retention: {
            title: "8. Retention Period of Personal Data",
            intro: "Your personal data is stored for the period required by the processing purpose and within legal retention periods:",
            items: [
              { type: "Customer data", duration: "Contract period + 10 years (as required by Tax Procedure Law)" },
              { type: "Contact data", duration: "Until communication ends + 2 years" },
              { type: "Marketing data", duration: "Until your explicit consent is withdrawn" },
              { type: "Technical data", duration: "2 years" },
              { type: "Legal retention periods", duration: "According to relevant legislation provisions" },
            ],
            note: "When these periods expire, your personal data is deleted, destroyed or anonymized in accordance with the law.",
          },
          security: {
            title: "9. Security of Personal Data",
            intro: "The following technical and administrative security measures are taken to ensure the security of your personal data:",
            items: [
              "Data transmission with SSL/TLS encryption technologies",
              "Secure server infrastructure and data centers",
              "Regular security updates and patches",
              "Access control and authorization mechanisms",
              "Regular backup and data recovery procedures",
              "Firewalls and intrusion detection systems",
              "Staff training and awareness activities",
              "Physical security measures",
            ],
          },
          rights: {
            title: "10. Rights of the Data Subject (KVKK Art. 11)",
            intro: "In accordance with KVKK Art. 11, you have the following rights:",
            items: [
              { title: "Request information", description: "Learning whether your personal data is processed, requesting information if processed" },
              { title: "Learn processing purpose", description: "Learning the purpose of processing and whether they are used in accordance with their purpose" },
              { title: "Domestic/international transfer information", description: "Knowing the third parties to whom personal data is transferred domestically or abroad" },
              { title: "Request correction", description: "Requesting correction if incomplete or incorrectly processed" },
              { title: "Request deletion/destruction", description: "Requesting deletion or destruction within the framework of conditions stipulated in KVKK Art. 7" },
              { title: "Notification to third parties", description: "Requesting that correction, deletion, destruction operations be notified to third parties to whom personal data is transferred" },
              { title: "Object", description: "Objecting to the emergence of a result against the person by analyzing the processed data exclusively through automated systems" },
              { title: "Request compensation for damage", description: "Requesting compensation for damage in case of damage due to unlawful processing of personal data" },
            ],
          },
          application: {
            title: "11. Application Method",
            paragraphs: [
              "To exercise your rights stated above, you can apply by the following methods with documents identifying your identity:",
            ],
            email: {
              title: "Application by Email:",
              address: "info@lakverse.com",
            },
            written: {
              title: "Written Application:",
              address: "LAKVERSE\nWorkinton Carousel, Fişekhane Cad. No: 5, Carousel AVM Floor 2B, 34710 Bakırköy, Istanbul, Türkiye",
            },
            note: "In your application, you must specify the explanations regarding the right and subject you are requesting. Your application will be finalized within 30 (thirty) days at the latest, depending on the nature of your request.",
            complaint: "If your application is rejected, the response given is found insufficient, or no response is given to the application within the time limit, you can file a complaint with the Personal Data Protection Board in accordance with KVKK Art. 14.",
          },
          changes: {
            title: "12. Changes",
            paragraphs: [
              "This Disclosure Text may be changed due to changes in legal regulations, updates in our business processes or new decisions of the Personal Data Protection Board. Important changes will be announced on our website.",
            ],
          },
          contact: {
            title: "13. Contact",
            paragraphs: [
              "You can contact us for questions about your rights under KVKK and the processing of your personal data:",
            ],
            company: "LAKVERSE",
            email: "Email",
            web: "Web",
          },
        },
      },
      services: {
        common: {
          features: "Features",
          process: {
            title: "Our Proven Process",
            subtitle: "",
          },
          steps: [
            {
              title: "Get Quote",
              description: "Get a free quote for your project and share your needs",
            },
            {
              title: "Plan",
              description: "Let's create the project plan together and determine the timeline",
            },
            {
              title: "Develop",
              description: "Our professional team develops your project while keeping you informed",
            },
            {
              title: "Publish",
              description: "Your project is ready! We publish it and provide support",
            },
          ],
        },
        webDesign: {
          title: "Web Design Services | Professional Website Design | LAK VERSE",
          description: "Modern, user-friendly and top-ranking web designs on Google. We design websites that look perfect on phone, tablet and computer and load quickly.",
          hero: {
            title: "Web Design",
            subtitle: "Elevate your brand in the digital world with modern, user-friendly and top-ranking web designs on Google",
          },
          features: [
            "Modern and user-friendly designs",
            "Top rankings on Google",
            "Perfect appearance on phone, tablet and computer",
            "Fast pages that load in seconds",
            "Custom, unique design solutions for your brand",
            "Interfaces that your customers can easily use",
          ],
          content: {
            title: "Professional Web Design Services",
            paragraphs: [
              "We design professional websites that reflect your brand's digital identity and increase your sales. With modern design principles and customer-focused approach, we create websites that will impress your visitors and help you achieve your business goals.",
              "In every project, we understand your brand's unique values and customer needs to develop custom design solutions. By also considering features such as top rankings on Google, perfect appearance on phone and tablet and fast loading, we design perfect websites both visually and in terms of usability.",
              "Our web design process begins with understanding your brand's customers. By examining customer research, competitor analysis and industry trends, we develop a custom design strategy for your brand. We create websites that look perfect on all devices, load quickly and your customers can easily use. By also considering the technical infrastructure needed for top rankings on Google in the design phase, we design superior websites both visually and in terms of performance.",
            ],
          },
        },
        webDevelopment: {
          title: "Web Development Services | Custom Web Software & Application Development | LAK VERSE",
          description: "Custom web software for your business: admin panels, customer portals, integrations and scalable web applications. We build fast, secure and sustainable solutions.",
          hero: {
            title: "Web Development",
            subtitle: "We build custom, fast, secure and scalable web software for your business",
          },
          features: [
            "Custom web software and applications for your business",
            "Admin panels and customer portals",
            "API development and system integrations",
            "Secure, scalable and easy-to-maintain infrastructure",
            "High performance and SEO-friendly structure",
            "Flawless experience on mobile and desktop",
          ],
          content: {
            title: "Professional Web Development Services",
            paragraphs: [
              "We build fast, secure and user-friendly web software that fits the way your business works. Where off-the-shelf packages fall short, we create fully custom solutions such as admin panels, customer portals, booking and ordering systems.",
              "We plan every detail, from the systems running behind your software to its connections with other platforms. With API development and integrations we bring the tools you use into a single structure, prioritizing data security and application performance from day one.",
              "The web software we build with modern technologies has an architecture that scales without problems, is easy to maintain and stays up to date. After launch, we stay with you for monitoring, improvements and new feature development.",
            ],
          },
        },
        mobileApp: {
          title: "Mobile App Development | iPhone and Android Applications | LAK VERSE",
          description: "Fast and user-friendly mobile applications for iPhone and Android phones. We offer reliable mobile solutions that your customers can easily use.",
          hero: {
            title: "Mobile App",
            subtitle: "Reach your customers anytime, anywhere with iPhone and Android applications",
          },
          features: [
            "Applications for iPhone and Android phones",
            "Single app option for both platforms",
            "Easy to use and elegant design",
            "Fast and smooth operation",
            "App Store and Google Play publishing support",
            "Compatible connections with other systems",
          ],
          content: {
            title: "Professional Mobile App Development",
            paragraphs: [
              "We develop fast and reliable mobile applications that your customers can easily use. We offer custom applications for iPhone and Android phones or a single app option for both platforms. We determine the most suitable solution for your business needs together.",
              "In every project, we understand platform features and customer expectations to develop custom mobile application solutions. We determine the most suitable option for your project's needs, whether custom applications for each platform or solutions that work on both platforms with a single app.",
              "Our mobile app development process begins with customer research and platform analysis. Using modern technologies, we develop fast and user-friendly mobile applications. We also provide support in App Store and Google Play publishing processes to ensure your app is successfully published.",
            ],
          },
        },
        desktopApp: {
          title: "Desktop Application Development | Windows, Mac and Linux Applications | LAK VERSE",
          description: "Modern and fast desktop applications for Windows, Mac and Linux. We develop reliable desktop solutions tailored to your business needs.",
          hero: {
            title: "Desktop App",
            subtitle: "Custom applications for your business needs for Windows, Mac and Linux",
          },
          features: [
            "Applications for Windows, Mac and Linux",
            "Single app that works on all operating systems",
            "Modern and easy-to-use interfaces",
            "Fast operation and security",
            "Ability to work without internet",
            "Compatible connections with other systems",
          ],
          content: {
            title: "Professional Desktop Application Development",
            paragraphs: [
              "We develop modern and fast desktop applications for Windows, Mac and Linux. We offer reliable desktop solutions tailored to your business needs.",
              "In every project, we understand your business needs and customer expectations to develop custom desktop application solutions. Using modern technologies, we create fast and secure desktop applications.",
              "Our desktop application development process begins with platform analysis and needs assessment. We develop desktop applications that work on all operating systems with a single app. We also provide support in working without internet connection, connections with other systems and security to offer custom desktop solutions for your business needs.",
            ],
          },
        },
        eCommerce: {
          title: "E-Commerce Solutions | Online Store Development | LAK VERSE",
          description: "Shopify, WooCommerce and custom e-commerce solutions. We build user-friendly and secure online stores that increase your sales.",
          hero: {
            title: "E-Commerce Systems",
            subtitle: "Strengthen your online sales channels and increase your sales",
          },
          features: [
            "Shopify and WooCommerce integrations",
            "Custom e-commerce solutions",
            "Secure payment systems",
            "Product management and inventory tracking",
            "SEO optimization and marketing tools",
            "Mobile-friendly and responsive design",
          ],
          content: {
            title: "Professional E-Commerce Solutions",
            paragraphs: [
              "We strengthen your online sales channels with Shopify, WooCommerce and custom e-commerce solutions. We build user-friendly and secure online stores that increase your sales.",
              "In every project, we analyze your business's product portfolio and sales strategy to develop custom e-commerce solutions. In addition to basic features such as secure payment systems, product management and inventory tracking, we also help increase your sales with SEO optimization and marketing tools.",
              "Our e-commerce development process begins with understanding your business's sales strategy and target audience. Using modern e-commerce platforms and custom solutions, we create user-friendly and secure online stores. With mobile-friendly and responsive design principles, we develop e-commerce sites that work perfectly on all devices.",
            ],
          },
        },
        shopify: {
          title: "Shopify Design and Development | Shopify Store Setup | LAK VERSE",
          description: "Shopify store design and development services. We set up your online store with professional Shopify themes and custom solutions.",
          hero: {
            title: "Shopify Design - Development",
            subtitle: "Increase your online sales with professional Shopify stores",
          },
          features: [
            "Custom Shopify store design for your brand",
            "Integration with Shopify apps",
            "Easy product management and catalog editing",
            "Secure payment and shipping connections",
            "Top rankings on Google",
            "Perfect appearance on phone, tablet and computer",
          ],
          content: {
            title: "Professional Shopify Design and Development",
            paragraphs: [
              "We offer Shopify store design and development services. We set up your online store with professional Shopify themes and custom solutions.",
              "In every project, we understand your brand's visual identity and product portfolio to develop custom Shopify theme designs. In addition to basic features such as Shopify app integrations, easy product management and catalog editing, we also ensure your store works smoothly with secure payment and shipping connections.",
              "Our Shopify development process begins with understanding your store's needs and customers. Using modern Shopify themes and custom solutions, we create user-friendly and secure online stores. We develop Shopify stores that will increase your sales with top rankings on Google and perfect appearance on phone, tablet and computer.",
            ],
          },
        },
        woocommerce: {
          title: "WooCommerce Design and Development | WordPress E-Commerce | LAK VERSE",
          description: "WooCommerce design and development services. We set up your online store with WordPress-based e-commerce solutions.",
          hero: {
            title: "WooCommerce Design - Development",
            subtitle: "Increase your online sales with WordPress-based e-commerce solutions",
          },
          features: [
            "Custom WooCommerce store design for your brand",
            "Custom features and plugins for your needs",
            "WordPress compatible and customizable structure",
            "Secure payment system integrations",
            "Easy inventory management and order tracking",
            "Top rankings on Google and security",
          ],
          content: {
            title: "Professional WooCommerce Design and Development",
            paragraphs: [
              "We offer WooCommerce design and development services. We set up your online store with WordPress-based e-commerce solutions.",
              "In every project, we understand your brand's visual identity and product portfolio to develop custom WooCommerce theme designs. In addition to basic features such as custom features and plugins, easy product management and catalog editing, we also ensure your store works smoothly with secure payment and shipping connections.",
              "Our WooCommerce development process begins with understanding your store's needs and customers. Using modern WooCommerce themes and custom solutions, we create user-friendly and secure online stores. We also provide expert support in top rankings on Google and security to develop WooCommerce stores that will increase your sales.",
            ],
          },
        },
        automation: {
          title: "Business Process Automation | Automation Solutions | LAK VERSE",
          description: "Automate your repetitive business processes. Save time and costs with system integrations, data synchronization, automated notifications and reporting.",
          hero: {
            title: "Automation",
            subtitle: "Automate repetitive work and free your team's time for work that creates value",
          },
          features: [
            "Business process automations tailored to you",
            "Integrations between CRM, ERP, e-commerce and other systems",
            "Automated notification and reporting systems",
            "Data processing, transformation and synchronization",
            "Scheduled tasks and event-triggered workflows",
            "AI-powered smart automations",
          ],
          content: {
            title: "Professional Automation Solutions",
            paragraphs: [
              "We automate your manual, repetitive business processes. From saving form submissions to your CRM to preparing invoices and reports automatically, we turn the work that consumes your team's time into systems that run for you.",
              "In every project we first analyze your current workflow and identify where automation adds the most value. We build secure connections between the software you use, keep your data up to date everywhere, and make your processes error-free and measurable.",
              "Depending on your needs, we use proven automation platforms or fully custom-built solutions. We monitor and continuously improve the automations we deliver, built with scheduled tasks, event-triggered workflows and AI-powered steps.",
            ],
          },
        },
        aiServices: {
          title: "AI Services | AI-Powered Solutions | LAK VERSE",
          description: "Photo, video and text generation services with artificial intelligence technologies. We develop custom AI integrations and productized services tailored to your business needs.",
          hero: {
            title: "AI Productized Services",
            subtitle: "Optimize your business processes with artificial intelligence technologies",
          },
          features: [
            "Content generation with artificial intelligence (text, image, video)",
            "Smart chatbots and customer services",
            "Compatible AI integrations with other systems",
            "Data analysis and smart reporting",
            "Custom AI solutions for your business",
            "Smart systems that automate your business processes",
          ],
          content: {
            title: "Professional AI Services",
            paragraphs: [
              "We offer photo, video and text generation services with artificial intelligence technologies. We develop custom AI integrations and ready-made services tailored to your business needs.",
              "In every project, we understand your business's AI needs and usage scenarios to develop custom AI solutions. In addition to basic features such as AI-powered content generation, photo and video processing, text analysis and generation, we also improve your customer experience with smart chatbots and customer support systems.",
              "Our AI service development process begins with understanding your business's AI needs and usage scenarios. Using modern AI technologies, we develop custom AI solutions and ready-made services. We also offer custom AI services for your business's unique needs with compatible integrations and custom solutions.",
            ],
          },
        },
        uiUx: {
          title: "UI & UX Design | User Interface and Experience Design | LAK VERSE",
          description: "User research, information architecture, wireframes, prototypes and interface design. We design easy-to-use, conversion-focused digital experiences for your web and mobile products.",
          hero: {
            title: "UI & UX Design",
            subtitle: "We design digital experiences your users love, find easy to use and that serve your business goals",
          },
          features: [
            "User research and personas",
            "Information architecture and user flows",
            "Wireframes and clickable prototypes",
            "Interface (UI) design for web and mobile",
            "Design systems and component libraries",
            "Usability testing and accessibility",
          ],
          content: {
            title: "Professional UI & UX Design Services",
            paragraphs: [
              "A good digital product does more than look good: it makes its users' work easier. With our UI & UX design service we design interfaces for websites, web software and mobile apps that are both visually appealing and intuitive to use.",
              "We start by understanding your users and business goals. After research and analysis we shape the information architecture and user flows, and use wireframes and clickable prototypes to make ideas testable before development begins.",
              "We turn approved flows into a consistent, accessible interface design that fits your brand. The design systems and component libraries we create speed up development and keep your design coherent as your product grows.",
            ],
          },
        },
      },
    },
  },
}

export function getTranslations(locale: Locale): Translations {
  // Ensure locale is valid, fallback to 'tr' if not
  const safeLocale: Locale = isValidLocale(locale) ? locale : 'tr'
  
  // Check if translations object exists and has the expected structure
  if (!translations || typeof translations !== 'object') {
    console.error('Translations object not loaded')
    return {} as Translations
  }
  
  // Check if the requested locale exists
  if (!translations[safeLocale]) {
    console.error('Translation not found for locale:', safeLocale, 'available locales:', Object.keys(translations))
    // Fallback to Turkish
    if (translations.tr) {
      return translations.tr
    }
    return {} as Translations
  }
  
  const translation = translations[safeLocale]
  
  // Verify translation has required structure
  if (!translation || typeof translation !== 'object') {
    console.error('Translation object is invalid for locale:', safeLocale, 'translation:', translation)
    if (translations.tr) {
      return translations.tr
    }
    return {} as Translations
  }
  
  // Debug: Log translation keys for troubleshooting
  if (process.env.NODE_ENV === 'development') {
    console.log('Translation keys for locale', safeLocale, ':', Object.keys(translation))
  }
  
  return translation
}

